import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { auth, db, storage } from '../firebase';
import { doc, getDoc, updateDoc, deleteDoc, deleteField, collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, Timestamp, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, uploadBytes } from 'firebase/storage';
import SyncedVideoPlayer from './SyncedVideoPlayer';
import JackSyncedPlotlyCharts from './JackSyncedPlotlyCharts';
import SyncedRouteMap from './SyncedRouteMap';
import SyncedControlBar from './SyncedControlBar';
import CommentsPanel from './CommentsPanel';
import BybTelemetryPanel from './BybTelemetryPanel';
import LayoutDropdown from './LayoutDropdown';
import BikeProfileAnalysisDropdown from './BikeProfileAnalysisDropdown';
import ResizableLayout from './ResizableLayout';
import RideStatsPanel from './RideStatsPanel';
import SuspensionAnalysisPanel from './SuspensionAnalysisPanel';
import { useAuth } from '../contexts/AuthContext';
import { useUploadStatus } from '../contexts/UploadStatusContext';
import { FaChartLine, FaMap, FaPlay, FaChevronLeft, FaChevronRight, FaSave, FaYoutube, FaCloudDownloadAlt, FaVideo, FaExternalLinkAlt, FaEllipsisV, FaTrash, FaColumns, FaTimes } from 'react-icons/fa';
import { getFunctions, httpsCallable } from 'firebase/functions';
import ImportSourceModal from './ImportSourceModal';
import ExportVideoModal from './ExportVideoModal';
import ModalPopup from './ModalPopup';
import FitParser from 'fit-file-parser';
import { recordVideoWithOverlays } from '../utils/clientExport';
import { runJackPackingAnalysisFromTelemetry, buildJackInputDebugSnapshot } from '../utils/jackPackingEngine';
import {
  getJackParamsForAnalysis,
  buildTrimOverrideForRide,
  getTelemetryTimeBounds,
  loadBikeProfilesStore,
} from '../utils/jackSettings';
import {
  serializeRunForOfflineDraft,
  deserializeRunFromOfflineDraft,
  enqueuePendingRunDraft,
  tryUploadPendingDraftOnce,
  TELEMETRY_OFFLOAD_JSON_LEN,
  isValidTelemetryDataDownloadUrl,
  getPendingRunDraft,
  syncPendingDraftById,
  discardLocalPendingRun,
  getPendingInlineRunByClientKey,
  updatePendingInlineRunByClientKey,
  removePendingQueueEntryByClientKey,
  updatePendingRunDraft,
} from '../utils/pendingRunsQueue';
import {
  patchCachesAfterPendingRunSynced,
  patchRunMetadataInUserRunListCaches,
  removeRunIdFromUserRunListCaches,
} from '../utils/runListCache';
import {
  enqueuePendingRunMetadataUpdate,
  clearPendingRunMetadataUpdateForRun,
} from '../utils/pendingRunMetadataQueue';
import { markRunTelemetryCachedOnDevice, clearRunTelemetryCachedOnDevice } from '../utils/runDeviceCache';
import { loadRunTelemetrySnapshot } from '../utils/runTelemetrySnapshot';
import { saveOfflineRunDetailCache, loadOfflineRunDetailCache } from '../utils/offlineRunDetailCache';
import { decodeShokiqBinaryToCsv } from '../utils/dataloggerSync';
import { parseBybTelemetryV3, bybBufferLooksLikeAppCsvExport } from '../utils/telemetryBybV3';
import {
  encodeTelemetrySamplesToBybArrayBuffer,
  encodeTelemetrySamplesToBybCsvText,
  sanitizeFilenameBase,
  validateBybArrayBufferForDownload,
} from '../utils/telemetryEncodeBybV3';
import { canViewRunClient, ownsRunFirestoreDoc } from '../utils/runAccess';
import {
  RIDE_PANEL_CONTENT_IDS,
  RIDE_PANEL_CATALOG,
  SUSPENSION_TAB_BY_CONTENT_ID,
  newPanelInstanceId,
  getDefaultPanelSlots,
  normalizePanelSlots,
  deriveLegacyPanelFlagsFromSlots,
  resolvePanelSlotsFromStorage,
  telemetryHasSuspensionSamples,
  isPanelContentRunnable,
} from '../utils/rideViewPanelCatalog';
import './RideView.css';

function readInitialRidePanelSlots() {
  try {
    const raw = localStorage.getItem('rideViewPanelStates');
    if (!raw) return getDefaultPanelSlots();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed.panelSlots) && parsed.panelSlots.length > 0) {
      const n = normalizePanelSlots(parsed.panelSlots);
      return n && n.length ? n : getDefaultPanelSlots();
    }
    const { slots, didMigrate } = resolvePanelSlotsFromStorage(parsed);
    if (didMigrate) {
      try {
        localStorage.removeItem('rideViewLayout');
      } catch {
        /* ignore */
      }
    }
    return slots && slots.length ? slots : getDefaultPanelSlots();
  } catch {
    return getDefaultPanelSlots();
  }
}

// YouTube API credentials (from Dashboard)
const CLIENT_ID = "493819678818-jpeodeohno8dq190calvns8010c3ieue.apps.googleusercontent.com";
const CLIENT_SECRET = "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";
const REFRESH_TOKEN = "1//04VqpgfqO57y4CgYIARAAGAQSNwF-L9Irv7fpvE_AcByYDDz82zB5EEMSFG9G5QpclEaRS6-1dN-GgqIsPHQL6eBAeK-cHBq-KB4";

const METRIC_LABELS = {
  time: 'Time',
  lat: 'Latitude',
  lon: 'Longitude',
  gps: 'GPS data',
  speed: 'Speed',
  elevation: 'Elevation',
  heartRate: 'Heart Rate',
  power: 'Power',
  cadence: 'Cadence',
  front_brake: 'Front Brake',
  rear_brake: 'Rear Brake',
  fork: 'Fork',
  shock: 'Shock',
  distance: 'Distance',
};

// Normalize value from row - chart uses heartRate, sources may use heart_rate
function getMergedVal(row, key) {
  let v = row?.[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (key === 'heartRate') v = row?.heart_rate;
  if (key === 'heart_rate') v = row?.heartRate;
  return (typeof v === 'number' && Number.isFinite(v)) ? v : null;
}

// Merge two telemetry arrays by time. choices: { key: 'keep'|'replace'|'import'|'ignore' }
function mergeTelemetryByTime(current, newData, choices) {
  if (!current?.length) return newData || [];
  const result = current.map((cur) => {
    const t = cur.time ?? 0;
    const nearest = newData.reduce((best, p) =>
      Math.abs((p.time ?? 0) - t) < Math.abs((best?.time ?? 0) - t) ? p : best
    );
    const merged = { ...cur };
    const curKeys = new Set(Object.keys(cur));
    const newKeys = new Set(Object.keys(nearest || {}));
    const allKeys = new Set([...curKeys, ...newKeys, 'heartRate', 'heart_rate', 'power', 'cadence', 'elevation', 'lat', 'lon']);
    allKeys.forEach((key) => {
      if (key === 'time') return;
      const choice = (key === 'lat' || key === 'lon')
        ? (choices.gps ?? choices[key])
        : (choices[key] ?? choices[key === 'heart_rate' ? 'heartRate' : key === 'heartRate' ? 'heart_rate' : key]);
      const curVal = key === 'lat' || key === 'lon' ? cur[key] : getMergedVal(cur, key);
      const newVal = key === 'lat' || key === 'lon' ? nearest?.[key] : getMergedVal(nearest, key);
      const curHas = typeof curVal === 'number' && Number.isFinite(curVal);
      const newHas = typeof newVal === 'number' && Number.isFinite(newVal);
      const outKey = key === 'heart_rate' ? 'heartRate' : key;
      if (choice === 'ignore') return;
      if (choice === 'replace' && newHas) {
        merged[outKey] = newVal;
      } else if (choice === 'import' && newHas) {
        merged[outKey] = newVal;
      } else if (!choice && !curHas && newHas) {
        merged[outKey] = newVal;
      } else if (choice === 'keep' && curHas) {
        merged[outKey] = curVal;
      }
    });
    return merged;
  });
  return result;
}

// Detect if a URL is a YouTube link (supports youtubeUrl or videoUrl containing YouTube)
const isYouTubeUrl = (url) => typeof url === 'string' && (url.includes('youtube.com') || url.includes('youtu.be'));

const GPX_XML_NS = ['http://www.topografix.com/GPX/1/1', 'http://www.topografix.com/GPX/1/0'];

function collectGpxTrkpts(xmlDoc) {
  let nodes = [...xmlDoc.getElementsByTagName('trkpt')];
  if (nodes.length === 0) {
    for (const ns of GPX_XML_NS) {
      nodes = [...xmlDoc.getElementsByTagNameNS(ns, 'trkpt')];
      if (nodes.length) break;
    }
  }
  return nodes;
}

function isTelemetryDataFile(file) {
  if (!file || !file.name) return false;
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv') || name.endsWith('.shq') || name.endsWith('.fit') || name.endsWith('.gpx') || name.endsWith('.byb')) return true;
  const t = (file.type || '').toLowerCase();
  if (t === 'text/csv' || t === 'application/vnd.ms-excel') return true;
  if (t === 'application/gpx+xml') return true;
  if ((t === 'application/xml' || t === 'text/xml') && name.endsWith('.gpx')) return true;
  return false;
}

function getFiniteMinMax(values) {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < values.length; i += 1) {
    const v = values[i];
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (min === Infinity || max === -Infinity) return null;
  return { min, max };
}

// Generate default run name with date/time
const getFormattedRunName = () => {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const yy = String(now.getFullYear()).slice(-2);
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  const sec = String(now.getSeconds()).padStart(2, '0');
  return `Run_${mm}-${dd}-${yy}_${hh}-${min}-${sec}`;
};

// Set to a number (e.g. 1) to limit export duration for testing; set to null for full export.

const RideView = () => {
  const { rideId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser } = useAuth();
  const user = authUser || auth.currentUser;
  const layoutRef = useRef(null);
  const unifiedFileInputRef = useRef(null);
  const locationStateRef = useRef(location.state);
  locationStateRef.current = location.state;
  const rideDataLoadGenRef = useRef(0);
  const uploadStatus = useUploadStatus();
  const { startExportVideo, updateExportVideo } = uploadStatus;
  
  // Detect if we're in creation mode
  const isCreationMode = rideId === 'create' || rideId === 'new';
  const isLocalPendingRide = typeof rideId === 'string' && rideId.startsWith('local-');
  const isQueuedInlineRide = typeof rideId === 'string' && rideId.startsWith('queued-inline-');
  /** No `runs/{rideId}` Firestore doc (local draft, inline queue card, create). */
  const isNonCloudRunId = isLocalPendingRide || isQueuedInlineRide;

  const [rideData, setRideData] = useState(null);
  const [loading, setLoading] = useState(true);
  /** Cloud run with no local/Firestore-offline copy when device is offline */
  const [offlineAccessBlocked, setOfflineAccessBlocked] = useState(false);
  const [addMetricsModal, setAddMetricsModal] = useState(null); // { newData, allNewMetrics, conflicts, choices }
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoOffset, setVideoOffset] = useState(0);
  const [dataOffset, setDataOffset] = useState(0);
  /** Jack Plotly x-axis range (data time s); set when user picks a packing event */
  const [jackPlotXZoom, setJackPlotXZoom] = useState(null);

  // Creation mode states
  const [runName, setRunName] = useState(getFormattedRunName());
  const [description, setDescription] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [telemetryFile, setTelemetryFile] = useState(null);
  const [editingName, setEditingName] = useState(false);
  /** View mode: click title to rename (owner / local / queued). */
  const [editingViewRunName, setEditingViewRunName] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importedActivityDate, setImportedActivityDate] = useState(null);
  const [saveVideoModalStep, setSaveVideoModalStep] = useState(null); // null | 'choice' | 'offline-warning'
  const [stravaConnected, setStravaConnected] = useState(false);
  const [brakeaceConnected, setBrakeaceConnected] = useState(false);
  
  const [panelSlots, setPanelSlots] = useState(readInitialRidePanelSlots);
  const [fetchingStravaTelemetry, setFetchingStravaTelemetry] = useState(false);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [deleteRunModalOpen, setDeleteRunModalOpen] = useState(false);
  const [headerMenuExportOpen, setHeaderMenuExportOpen] = useState(false);
  const headerMenuRef = useRef(null);
  const [comments, setComments] = useState([]);
  const [trainingZones, setTrainingZones] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportMessage, setExportMessage] = useState(null);
  const [exportStarting, setExportStarting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null); // 'uploading' | 'starting' | null
  const [chartVisibleMetrics, setChartVisibleMetrics] = useState(() => ({
    speed: true, frontBrake: true, rearBrake: true, heartRate: true,
    power: true, elevation: true, distance: true, fork: true, shock: true, cadence: true,
  }));
  const exportJobUnsubscribeRef = useRef(null);
  const videoDurationReportedRef = useRef(false);
  const [videoDuration, setVideoDuration] = useState(0);
  /** Saved profile id for Jack / suspension analysis on this screen only; null = use Settings (loadMainJackSettings). */
  const [analysisBikeProfileId, setAnalysisBikeProfileId] = useState(null);

  useEffect(() => {
    setAnalysisBikeProfileId(null);
  }, [rideId]);

  useEffect(() => {
    setEditingViewRunName(false);
  }, [rideId]);

  const dataDuration = React.useMemo(() => {
    const td = rideData?.telemetryData;
    if (!Array.isArray(td) || td.length === 0) return 0;
    let min = Infinity;
    let max = -Infinity;
    for (let i = 0; i < td.length; i++) {
      const t = td[i]?.time;
      if (typeof t === 'number' && Number.isFinite(t)) {
        min = Math.min(min, t);
        max = Math.max(max, t);
      }
    }
    if (max < 0) return 0;
    // If times look like timestamps (min > 60), use span; else 0-based → use max
    return min > 60 ? Math.max(0, max - min) : max;
  }, [rideData?.telemetryData]);

  /** Used to default the Stats panel: on when GPS exists, off when not (unless user preference is saved). */
  const telemetryHasGps = useMemo(() => {
    const td = rideData?.telemetryData;
    if (!Array.isArray(td) || td.length === 0) return false;
    return td.some((d) => d && d.lat && d.lon);
  }, [rideData?.telemetryData]);

  const telemetrySuspensionOk = useMemo(
    () => telemetryHasSuspensionSamples(rideData?.telemetryData),
    [rideData?.telemetryData],
  );

  const updateSlotContent = useCallback((slotId, contentId) => {
    if (!RIDE_PANEL_CATALOG[contentId]) return;
    setPanelSlots((prev) => prev.map((s) => (s.id === slotId ? { ...s, contentId } : s)));
  }, []);

  const removePanelSlot = useCallback((slotId) => {
    setPanelSlots((prev) => (prev.length <= 1 ? prev : prev.filter((s) => s.id !== slotId)));
  }, []);

  const addPanelSlot = useCallback(() => {
    setPanelSlots((prev) => [...prev, { id: newPanelInstanceId(), contentId: 'charts' }]);
  }, []);

  const panelAvailCtx = useMemo(
    () => ({
      hasTelemetry: Array.isArray(rideData?.telemetryData) && rideData.telemetryData.length > 0,
      hasGps: telemetryHasGps,
      hasSuspensionSamples: telemetrySuspensionOk,
      includeVideoPlaceholder: true,
    }),
    [rideData?.telemetryData, telemetryHasGps, telemetrySuspensionOk],
  );

  const ridePanelTitleContent = useMemo(() => {
    const singleSlotMode = panelSlots.length === 1;
    const out = {};
    panelSlots.forEach((slot) => {
      if (singleSlotMode) {
        out[slot.id] = (
          <div
            className="ride-panel-header-tabs"
            role="tablist"
            aria-label="Panel content"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {RIDE_PANEL_CONTENT_IDS.map((cid) => {
              const def = RIDE_PANEL_CATALOG[cid];
              const TabIcon = def?.icon || FaColumns;
              const ok = isPanelContentRunnable(cid, panelAvailCtx);
              const active = slot.contentId === cid;
              return (
                <button
                  key={cid}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-disabled={!ok}
                  disabled={!ok}
                  title={def?.label ?? cid}
                  className={`ride-panel-header-tab ${active ? 'ride-panel-header-tab--active' : ''} ${!ok ? 'ride-panel-header-tab--disabled' : ''}`}
                  onClick={() => ok && updateSlotContent(slot.id, cid)}
                >
                  <TabIcon className="ride-panel-header-tab-icon" aria-hidden size={12} />
                  <span className="ride-panel-header-tab-label">{def?.label ?? cid}</span>
                </button>
              );
            })}
          </div>
        );
        return;
      }
      const Icon = RIDE_PANEL_CATALOG[slot.contentId]?.icon || FaColumns;
      out[slot.id] = (
        <div className="ride-panel-header-title-row">
          <Icon className="ride-panel-header-type-icon" aria-hidden size={14} />
          <label
            className="ride-panel-header-select-wrap"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <select
              className="ride-panel-content-select"
              value={slot.contentId}
              onChange={(e) => updateSlotContent(slot.id, e.target.value)}
              aria-label="Panel content"
            >
              {RIDE_PANEL_CONTENT_IDS.map((cid) => {
                const ok = isPanelContentRunnable(cid, panelAvailCtx);
                return (
                  <option key={cid} value={cid} disabled={!ok}>
                    {RIDE_PANEL_CATALOG[cid].label}
                  </option>
                );
              })}
            </select>
          </label>
        </div>
      );
    });
    return out;
  }, [panelSlots, panelAvailCtx, updateSlotContent]);

  const canRenameInView = useMemo(
    () =>
      !!user &&
      !!rideData &&
      !isCreationMode &&
      (isLocalPendingRide ||
        isQueuedInlineRide ||
        (typeof rideId === 'string' &&
          !rideId.startsWith('local-') &&
          !rideId.startsWith('queued-inline-') &&
          ownsRunFirestoreDoc(user.uid, rideData))),
    [user, rideData, isCreationMode, isLocalPendingRide, isQueuedInlineRide, rideId],
  );

  const jackRideParams = React.useMemo(() => {
    const td = rideData?.telemetryData;
    const base = rideData?.jackSuspensionParams ?? rideData?.suspensionParams ?? {};
    const trim = buildTrimOverrideForRide(Array.isArray(td) ? td : [], rideData?.analysisTrim);
    let out = { ...base, ...trim };
    if (typeof analysisBikeProfileId === 'string' && analysisBikeProfileId) {
      const p = loadBikeProfilesStore().profiles.find((x) => x.id === analysisBikeProfileId);
      if (p) {
        const linkageSource =
          typeof p.linkageSource === 'string' && p.linkageSource.length > 0 ? p.linkageSource : 'custom';
        out = {
          ...out,
          frontTravel: Math.round(Number(p.frontTravel)) || 0,
          rearTravel: Math.round(Number(p.rearTravel)) || 0,
          rearA: Number(p.rearA) || 0,
          rearB: Number(p.rearB) || 0,
          rearC: Number(p.rearC) || 0,
          rearD: Number(p.rearD) || 0,
          linkageSource,
        };
      }
    }
    return out;
  }, [
    rideData?.telemetryData,
    rideData?.jackSuspensionParams,
    rideData?.suspensionParams,
    rideData?.analysisTrim,
    analysisBikeProfileId,
  ]);

  const jackAnalysisForRide = React.useMemo(() => {
    const td = rideData?.telemetryData;
    if (!Array.isArray(td) || td.length === 0) return null;
    const params = getJackParamsForAnalysis(jackRideParams);
    return runJackPackingAnalysisFromTelemetry(td, params);
  }, [rideData?.telemetryData, jackRideParams]);

  const telemetryTimeBounds = React.useMemo(
    () => getTelemetryTimeBounds(rideData?.telemetryData),
    [rideData?.telemetryData],
  );

  const jackChartAnalysisWindow = React.useMemo(() => {
    const p = getJackParamsForAnalysis(jackRideParams);
    const end =
      p.trimEnd === Infinity || !Number.isFinite(Number(p.trimEnd))
        ? telemetryTimeBounds.maxT
        : Number(p.trimEnd);
    return { start: Number(p.trimStart), end };
  }, [jackRideParams, telemetryTimeBounds.maxT]);

  /** Dev: compare Jack inputs with Open Packing Analyzer (`jack-benchmark.html`) — filter console `[ShokIQ Jack calculation inputs]`. */
  useEffect(() => {
    const td = rideData?.telemetryData;
    if (!Array.isArray(td) || td.length === 0) return;
    const mergedFromApp = getJackParamsForAnalysis(jackRideParams);
    const sortKeysSerializable = (obj) => {
      const o = {};
      for (const k of Object.keys(obj || {}).sort()) {
        const v = obj[k];
        o[k] = v === Infinity ? 'Infinity' : v === -Infinity ? '-Infinity' : v;
      }
      return o;
    };
    const sampleRow = td[0] && typeof td[0] === 'object' ? td[0] : {};
    const telemetryColumns = Object.keys(sampleRow).sort();
    const effectiveSnap = buildJackInputDebugSnapshot(mergedFromApp);
    const paramsFingerprint = JSON.stringify(effectiveSnap);
    let forkShockAdcSanity = null;
    if (td.length > 0) {
      let fmin = Infinity;
      let fmax = -Infinity;
      let rmin = Infinity;
      let rmax = -Infinity;
      for (let i = 0; i < td.length; i += 1) {
        const row = td[i];
        const f = Number(row?.fork ?? row?.front_disp ?? row?.front);
        const r = Number(row?.shock ?? row?.rear_disp ?? row?.rear);
        if (Number.isFinite(f)) {
          fmin = Math.min(fmin, f);
          fmax = Math.max(fmax, f);
        }
        if (Number.isFinite(r)) {
          rmin = Math.min(rmin, r);
          rmax = Math.max(rmax, r);
        }
      }
      if (Number.isFinite(fmin) && Number.isFinite(rmin)) {
        forkShockAdcSanity = {
          usedFrontKey: Object.prototype.hasOwnProperty.call(sampleRow, 'fork')
            ? 'fork'
            : Object.prototype.hasOwnProperty.call(sampleRow, 'front_disp')
              ? 'front_disp'
              : 'front',
          usedRearKey: Object.prototype.hasOwnProperty.call(sampleRow, 'shock')
            ? 'shock'
            : Object.prototype.hasOwnProperty.call(sampleRow, 'rear_disp')
              ? 'rear_disp'
              : 'rear',
          frontMin: fmin,
          frontMax: fmax,
          rearMin: rmin,
          rearMax: rmax,
        };
      }
    }
    const payload = {
      source: 'RideView',
      flow: isCreationMode ? 'ride_creation' : 'saved_ride',
      telemetryRowCount: td.length,
      /** DAQ parse maps CSV → fewer in-memory keys; Jack still reads fork/shock (same counts as raw CSV). */
      telemetryColumns,
      forkShockAdcSanity,
      jackRideParamsOverride: { ...jackRideParams },
      mergedFromApp_getJackParamsForAnalysis: sortKeysSerializable(mergedFromApp),
      effectiveAfterSanitize_jackEngine: effectiveSnap,
      paramsFingerprint,
    };
    console.log('[ShokIQ Jack calculation inputs — Ride]', payload);
    console.log('[ShokIQ Jack params fingerprint — Ride]', paramsFingerprint);
    window.__SHOKIQ_JACK_INPUTS_RIDE__ = payload;
  }, [rideData?.telemetryData, jackRideParams, isCreationMode]);

  const controlBarAnalysisTrim = React.useMemo(() => {
    if (!rideData || !(telemetryTimeBounds.maxT > telemetryTimeBounds.minT)) return null;
    return {
      minTime: telemetryTimeBounds.minT,
      maxTime: telemetryTimeBounds.maxT,
      startTime:
        rideData.analysisTrim?.startTime != null ? rideData.analysisTrim.startTime : telemetryTimeBounds.minT,
      endTime:
        rideData.analysisTrim?.endTime != null ? rideData.analysisTrim.endTime : telemetryTimeBounds.maxT,
    };
  }, [rideData, telemetryTimeBounds.minT, telemetryTimeBounds.maxT]);

  const handleAnalysisTrimChange = useCallback((next) => {
    setRideData((prev) => (prev ? { ...prev, analysisTrim: { ...next } } : prev));
    setHasUnsavedChanges(true);
  }, []);

  // Initialize with files from navigation state if in creation mode
  useEffect(() => {
    if (isCreationMode && location.state?.files && Array.isArray(location.state.files)) {
      const files = location.state.files;
      files.forEach(file => {
        if (file.type.startsWith('video/')) {
          setVideoFile(file);
          setVideoPreviewUrl(URL.createObjectURL(file));
        } else if (isTelemetryDataFile(file)) {
          setTelemetryFile(file);
          parseTelemetryFile(file);
        }
      });
      setLoading(false);
      return undefined;
    }
    if (isLocalPendingRide) {
      let cancelled = false;
      (async () => {
        try {
          setLoading(true);
          const localDraftId = rideId.slice('local-'.length);
          const raw = await getPendingRunDraft(localDraftId);
          if (cancelled) return;
          if (!raw) {
            alert('Local run not found or already synced.');
            navigate(user ? '/my-rides' : '/home');
            return;
          }
          const data = deserializeRunFromOfflineDraft(raw);
          setRideData({
            id: rideId,
            ...data,
            pendingSync: true,
            localDraftId,
          });
          setRunName(data.runName || data.name || getFormattedRunName());
          setDescription(typeof data.description === 'string' ? data.description : '');
          if (location.state?.localVideoUrl) {
            setVideoPreviewUrl(location.state.localVideoUrl);
          }
          const vo = typeof data.videoOffset === 'number' ? data.videoOffset : 0;
          const dOff = typeof data.dataOffset === 'number' ? data.dataOffset : 0;
          setVideoOffset(vo);
          setDataOffset(dOff);

          const td = data.telemetryData;
          if (Array.isArray(td) && td.length > 0) {
            let minT = Infinity;
            let maxT = -Infinity;
            for (let i = 0; i < td.length; i++) {
              const t = td[i]?.time;
              if (typeof t === 'number' && Number.isFinite(t)) {
                minT = Math.min(minT, t);
                maxT = Math.max(maxT, t);
              }
            }
            if (maxT >= 0) {
              const durationSec = minT > 60 ? Math.max(0, maxT - minT) : maxT;
              const hasVideoSource = !!location.state?.localVideoUrl;
              if (!hasVideoSource && durationSec > 0) setDuration(durationSec);
            }
          }
        } catch (e) {
          console.error('Failed to load local pending run:', e);
          if (!cancelled) {
            alert('Could not load local run.');
            navigate(user ? '/my-rides' : '/home');
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }
    if (!isCreationMode) {
      loadRideData();
      return undefined;
    }
    setLoading(false);
    return undefined;
  }, [rideId, user, navigate, isCreationMode, isLocalPendingRide, location.pathname]);

  useEffect(() => {
    setOfflineAccessBlocked(false);
  }, [rideId]);

  // Load Strava/BrakeAce connection status for import options (creation mode only)
  useEffect(() => {
    if (!isCreationMode || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const [stravaSnap, brakeaceSnap] = await Promise.all([
          getDoc(doc(db, 'stravaTokens', user.uid)),
          getDoc(doc(db, 'brakeaceTokens', user.uid)),
        ]);
        if (!cancelled) {
          setStravaConnected(!!stravaSnap?.exists?.());
          setBrakeaceConnected(!!brakeaceSnap?.exists?.());
        }
      } catch {
        if (!cancelled) {
          setStravaConnected(false);
          setBrakeaceConnected(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isCreationMode, user]);

  // Open import modal on BrakeAce when navigated from MyRides "Sync with BrakeAce"
  useEffect(() => {
    if (isCreationMode && location.state?.openImport === 'brakeace') {
      setShowImportModal(true);
    }
  }, [isCreationMode, location.state?.openImport]);

  // Save agnostic panel slots (+ legacy show* flags for Compare pane) to localStorage
  useEffect(() => {
    try {
      let prev = {};
      try {
        prev = JSON.parse(localStorage.getItem('rideViewPanelStates') || '{}');
      } catch {
        prev = {};
      }
      const panelStates = {
        ...prev,
        panelSlots,
        ...deriveLegacyPanelFlagsFromSlots(panelSlots),
      };
      localStorage.setItem('rideViewPanelStates', JSON.stringify(panelStates));
    } catch (error) {
      console.error('Failed to save panel states:', error);
    }
  }, [panelSlots]);

  // Load training zones for the current user (if available)
  useEffect(() => {
    if (!user) {
      setTrainingZones(null);
      return;
    }

    let isCancelled = false;
    async function loadZones() {
      try {
        // Rides default to cycling zones unless explicitly tagged in data
        const sport = rideData?.sport || 'cycling';
        const zonesQuery = query(
          collection(db, 'trainingZones'),
          where('userId', '==', user.uid),
          where('sport', '==', sport)
        );
        const snapshot = await getDocs(zonesQuery);
        if (isCancelled) return;
        if (!snapshot.empty) {
          setTrainingZones(snapshot.docs[0].data());
        } else {
          setTrainingZones(null);
        }
      } catch (error) {
        console.error('Error loading training zones:', error);
        if (!isCancelled) setTrainingZones(null);
      }
    }

    loadZones();
    return () => {
      isCancelled = true;
    };
  }, [user, rideData?.sport]);

  // Load comments from Firebase (only in view mode)
  useEffect(() => {
    if (!rideId || isCreationMode || isNonCloudRunId) return;
    if (!user) return;

    const commentsRef = collection(db, 'runs', rideId, 'comments');
    const q = query(commentsRef, orderBy('timestamp', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const commentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setComments(commentsData);
    }, (error) => {
      console.error('Error loading comments:', error);
    });

    return () => unsubscribe();
  }, [rideId, isCreationMode, isNonCloudRunId, user]);

  // When video is pending (upload in progress), listen for run doc updates so youtubeUrl appears without refresh
  useEffect(() => {
    if (!rideId || isCreationMode || isNonCloudRunId || !rideData?.id) return;
    if (!rideData.videoFileName || rideData.youtubeUrl || rideData.youtube_url) return;

    const runRef = doc(db, 'runs', rideId);
    const unsubscribe = onSnapshot(runRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      const youtubeUrl = data.youtubeUrl || data.youtube_url || (data.videoUrl && isYouTubeUrl(data.videoUrl) ? data.videoUrl : null);
      if (youtubeUrl) {
        setRideData((prev) => (prev ? { ...prev, youtubeUrl, youtube_url: youtubeUrl } : prev));
        setVideoPreviewUrl(youtubeUrl);
      }
    }, (err) => console.error('Run snapshot (video pending):', err));

    return () => unsubscribe();
  }, [rideId, isCreationMode, isNonCloudRunId, rideData?.id, rideData?.videoFileName, rideData?.youtubeUrl, rideData?.youtube_url]);

  const loadRideData = useCallback(async () => {
    try {
      setLoading(true);
      videoDurationReportedRef.current = false;

      if (typeof rideId === 'string' && rideId.startsWith('local-')) {
        setLoading(false);
        return;
      }

      if (typeof rideId === 'string' && rideId.startsWith('queued-inline-')) {
        const clientKey = rideId.slice('queued-inline-'.length);
        const payload = getPendingInlineRunByClientKey(clientKey);
        if (!payload?.runData) {
          if (user?.uid) removeRunIdFromUserRunListCaches(user.uid, rideId);
          alert('This queued run is no longer on this device.');
          navigate(user ? '/my-rides' : '/home');
          setLoading(false);
          return;
        }
        const rd = payload.runData;
        setRideData({
          ...rd,
          id: rideId,
          pendingSync: true,
          pendingClientKey: clientKey,
        });
        setRunName(rd.runName || rd.name || '');
        const anyUrl =
          typeof rd.videoUrl === 'string' && rd.videoUrl.trim()
            ? rd.videoUrl.trim()
            : typeof rd.youtubeUrl === 'string' && rd.youtubeUrl.trim()
              ? rd.youtubeUrl.trim()
              : null;
        if (anyUrl) setVideoPreviewUrl(anyUrl);
        setLoading(false);
        return;
      }

      rideDataLoadGenRef.current += 1;
      const loadGen = rideDataLoadGenRef.current;

      setRideData((prev) => {
        if (prev && String(prev.id) !== String(rideId)) {
          return null;
        }
        return prev;
      });

      const nav = locationStateRef.current;
      if (nav?.localVideoUrl) {
        setVideoPreviewUrl(nav.localVideoUrl);
      }

      // Ensure Firestore sees a fresh Auth token (avoids permission-denied right after login / tab wake).
      try {
        if (auth.currentUser) {
          await auth.currentUser.getIdToken(false);
        }
      } catch (tokenErr) {
        console.warn('Auth token before run load:', tokenErr);
      }

      setOfflineAccessBlocked(false);

      let rideDocSnap = null;
      let getDocFailed = false;
      try {
        rideDocSnap = await getDoc(doc(db, 'runs', rideId));
      } catch (readErr) {
        getDocFailed = true;
        console.warn('[RideView] getDoc error:', readErr?.code || readErr);
      }

      const isCloudRideId =
        typeof rideId === 'string' && !rideId.startsWith('local-') && !rideId.startsWith('queued-inline-');
      const offline = typeof navigator !== 'undefined' && !navigator.onLine;

      let data = null;
      if (rideDocSnap?.exists()) {
        data = { ...rideDocSnap.data(), id: rideDocSnap.id };
      } else if (offline && user?.uid && isCloudRideId) {
        const meta = await loadOfflineRunDetailCache(user.uid, rideId);
        if (meta && typeof meta === 'object') {
          data = { ...meta, id: rideId };
          const snap = await loadRunTelemetrySnapshot(user.uid, rideId);
          if (Array.isArray(snap) && snap.length > 0) {
            data.telemetryData = snap;
          }
        }
      }

      if (!data) {
        if (offline && user?.uid && isCloudRideId) {
          setRideData(null);
          setOfflineAccessBlocked(true);
          setLoading(false);
          return;
        }
        if (getDocFailed) {
          alert(
            'Failed to load run data' +
              (typeof navigator !== 'undefined' && navigator.onLine ? ': could not reach the server.' : ''),
          );
          setLoading(false);
          return;
        }
        if (user?.uid) removeRunIdFromUserRunListCaches(user.uid, rideId);
        alert('Run not found (removed from your saved list on this device).');
        navigate(user ? '/my-rides' : '/home');
        return;
      }

      if (!canViewRunClient(user, data)) {
        if (!user) {
          navigate('/login', { state: { from: location.pathname } });
        } else {
          alert('You do not have permission to view this run');
          navigate('/my-rides');
        }
        return;
      }

      // Merge with local state if available (use ref — do not depend on location.state in loadRideData deps)
      if (nav?.localVideoUrl && !data.videoUrl) {
        data.localVideoUrl = nav.localVideoUrl;
      }
      if (nav?.telemetryData && nav.telemetryData.length > 0) {
        data.telemetryData = nav.telemetryData;
      }
      if (data.telemetryDataUrl != null && !isValidTelemetryDataDownloadUrl(data.telemetryDataUrl)) {
        delete data.telemetryDataUrl;
      }
      let stripTelemetryUrlFromServer = false;
      if (
        typeof navigator !== 'undefined' &&
        navigator.onLine &&
        (!Array.isArray(data.telemetryData) || data.telemetryData.length === 0) &&
        isValidTelemetryDataDownloadUrl(data.telemetryDataUrl)
      ) {
        const url = data.telemetryDataUrl;
        try {
          const remoteTelemetryResp = await fetch(url);
          if (remoteTelemetryResp.ok) {
            const text = await remoteTelemetryResp.text();
            const trimmed = typeof text === 'string' ? text.trim() : '';
            if (trimmed && trimmed !== 'undefined' && trimmed !== 'null') {
              try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed)) {
                  data.telemetryData = parsed;
                } else {
                  stripTelemetryUrlFromServer = true;
                }
              } catch (parseErr) {
                console.warn(
                  'telemetryDataUrl response is not valid JSON:',
                  parseErr?.message,
                  String(trimmed || '').slice(0, 160),
                );
                stripTelemetryUrlFromServer = true;
              }
            } else {
              stripTelemetryUrlFromServer = true;
            }
          } else {
            stripTelemetryUrlFromServer = true;
          }
        } catch (err) {
          console.warn('Failed to load telemetryDataUrl:', err);
          stripTelemetryUrlFromServer = true;
        }
      }

      if ((!Array.isArray(data.telemetryData) || data.telemetryData.length === 0) && user?.uid) {
        const snap = await loadRunTelemetrySnapshot(user.uid, rideId);
        if (Array.isArray(snap) && snap.length > 0) {
          data.telemetryData = snap;
        }
      }

      if (
        stripTelemetryUrlFromServer &&
        typeof navigator !== 'undefined' &&
        navigator.onLine &&
        user?.uid &&
        typeof rideId === 'string' &&
        !rideId.startsWith('local-') &&
        !rideId.startsWith('queued-inline-')
      ) {
        try {
          await updateDoc(doc(db, 'runs', rideId), { telemetryDataUrl: deleteField() });
        } catch (e) {
          console.warn('Could not remove corrupt telemetryDataUrl from run doc:', e);
        }
        delete data.telemetryDataUrl;
      }

      // Check if upload was cancelled - prompt user to find video file
      if (data.uploadCancelled && data.videoFileName && !data.videoUrl && !nav?.localVideoUrl) {
        alert(`Video upload was cancelled for this run.\n\nPlease find and upload the video file: "${data.videoFileName}"\n\nYou can drop the video file on this page to view it.`);
      }

      // Normalize YouTube: ensure we have youtubeUrl set if the run has a YouTube link anywhere (support both camelCase and snake_case)
      const rawUrl = data.videoUrl || data.video_url || data.localVideoUrl || data.videos?.[0]?.url || data.youtubeUrl || data.youtube_url;
      const anyUrl = typeof rawUrl === 'string' && rawUrl.trim() ? rawUrl.trim() : null;
      if (anyUrl && isYouTubeUrl(anyUrl)) {
        data.youtubeUrl = data.youtubeUrl || data.youtube_url || anyUrl;
      }
      if (anyUrl && !data.videoUrl && data.video_url) data.videoUrl = data.video_url;
      if (loadGen !== rideDataLoadGenRef.current) {
        return;
      }
      if (user?.uid && typeof rideId === 'string' && !rideId.startsWith('local-') && !rideId.startsWith('queued-inline-')) {
        if (Array.isArray(data.telemetryData) && data.telemetryData.length > 0) {
          markRunTelemetryCachedOnDevice(user.uid, rideId);
        } else {
          clearRunTelemetryCachedOnDevice(user.uid, rideId);
        }
      }
      if (typeof navigator !== 'undefined' && navigator.onLine && user?.uid && isCloudRideId) {
        saveOfflineRunDetailCache(user.uid, rideId, data).catch((err) =>
          console.warn('[RideView] saveOfflineRunDetailCache:', err),
        );
      }
      setRideData(data);
      setRunName(data.runName || data.name || '');
      // Keep video preview in sync with loaded ride so video player has a URL
      if (anyUrl) {
        setVideoPreviewUrl(anyUrl);
      }
      // Load offsets from Firebase
      setVideoOffset(data.videoOffset || 0);
      setDataOffset(data.dataOffset || 0);
      
      // Set duration from data: prefer telemetry (most accurate), then data.duration, then durationSec / Strava
      // When we have video (anyUrl), NEVER set from data — video is source of truth
      let durationSec = null;
      if (Array.isArray(data.telemetryData) && data.telemetryData.length > 0) {
        let minT = Infinity;
        let maxT = -Infinity;
        for (let i = 0; i < data.telemetryData.length; i++) {
          const t = data.telemetryData[i]?.time;
          if (typeof t === 'number' && Number.isFinite(t)) {
            minT = Math.min(minT, t);
            maxT = Math.max(maxT, t);
          }
        }
        // If times look like timestamps (min > 60), use span; else 0-based → use max
        durationSec = maxT >= 0 ? (minT > 60 ? Math.max(0, maxT - minT) : maxT) : null;
      }
      if (durationSec == null && data.duration != null && typeof data.duration === 'number') {
        durationSec = data.duration;
      }
      if (durationSec == null) {
        durationSec = data.durationSec ?? data.strava?.movingTimeSeconds ?? (typeof data.strava?.elapsedTimeSeconds === 'number' ? data.strava.elapsedTimeSeconds : null);
      }
      if (durationSec != null && Number.isFinite(durationSec) && !videoDurationReportedRef.current && !anyUrl) {
        setDuration(durationSec);
      }

    } catch (error) {
      console.error('Error loading ride:', error);
      const isPermissionDenied = error?.code === 'permission-denied' || (error?.message && error.message.includes('permission'));
      if (isPermissionDenied) {
        if (!user) {
          alert('This run is private. Please log in to view it.');
          navigate('/login', { state: { from: location.pathname } });
        } else {
          alert('You do not have permission to view this run.');
          navigate('/my-rides');
        }
      } else {
        alert('Failed to load run data' + (error?.message ? ': ' + error.message : ''));
      }
    } finally {
      setLoading(false);
    }
  }, [rideId, user, navigate, location.pathname]);

  const handleFetchStravaTelemetry = useCallback(async () => {
    const activityId = rideData?.strava?.activityId ?? rideData?.strava?.activity_id;
    if (!rideId || !activityId || !user) return;
    if (typeof rideId === 'string' && (rideId.startsWith('local-') || rideId.startsWith('queued-inline-'))) return;
    setFetchingStravaTelemetry(true);
    try {
      const fn = httpsCallable(getFunctions(), 'stravaImportActivity');
      const res = await fn({ activityId: typeof activityId === 'string' ? parseInt(activityId, 10) : activityId });
      const data = res?.data;
      if (!data?.telemetryData || !Array.isArray(data.telemetryData) || data.telemetryData.length === 0) {
        alert('Strava returned no stream data for this activity. It may be manual or not have GPS/speed recorded.');
        return;
      }
      const runRef = doc(db, 'runs', rideId);
      await updateDoc(runRef, {
        telemetryData: data.telemetryData,
        updatedAt: Date.now(),
      });
      let minT = Infinity;
      let maxT = -Infinity;
      for (let i = 0; i < data.telemetryData.length; i++) {
        const t = data.telemetryData[i]?.time;
        if (typeof t === 'number' && Number.isFinite(t)) {
          minT = Math.min(minT, t);
          maxT = Math.max(maxT, t);
        }
      }
      const hasVideoSource = !!(videoPreviewUrl || rideData?.videoUrl || rideData?.youtubeUrl);
      if (!hasVideoSource && maxT >= 0) {
        const dur = minT > 60 ? Math.max(0, maxT - minT) : maxT;
        if (dur > 0) setDuration(dur);
      }
      setRideData((prev) => (prev ? { ...prev, telemetryData: data.telemetryData } : prev));
      markRunTelemetryCachedOnDevice(user.uid, rideId);
    } catch (err) {
      console.error('Fetch Strava telemetry failed:', err);
      const msg = err?.message || err?.details?.message || String(err);
      alert('Failed to fetch GPS/speed from Strava: ' + msg);
    } finally {
      setFetchingStravaTelemetry(false);
    }
  }, [rideId, rideData?.strava?.activityId, rideData?.strava?.activity_id, rideData?.videoUrl, rideData?.youtubeUrl, user, videoPreviewUrl]);

  useEffect(() => {
    if (!headerMenuOpen) return;
    const onDocClick = (e) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(e.target)) setHeaderMenuOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [headerMenuOpen]);

  useEffect(() => {
    if (!headerMenuOpen) setHeaderMenuExportOpen(false);
  }, [headerMenuOpen]);

  useEffect(() => {
    if (!rideData?.pendingSync || !rideData?.localDraftId || !user) return undefined;
    const draftId = rideData.localDraftId;
    let cancelled = false;

    const trySync = async () => {
      if (!navigator.onLine) return;
      const rawDraft = await getPendingRunDraft(draftId);
      const newId = await syncPendingDraftById(db, user, draftId);
      if (cancelled || !newId) return;
      if (rawDraft) {
        try {
          const runForUi = deserializeRunFromOfflineDraft(rawDraft);
          patchCachesAfterPendingRunSynced(user.uid, draftId, newId, runForUi);
        } catch (e) {
          console.warn('Cache patch after sync skipped:', e);
        }
      }
      navigate(`/ride/${newId}`, {
        replace: true,
        state: {
          localVideoUrl: videoPreviewUrl,
          videoFile,
          telemetryData: rideData?.telemetryData || [],
        },
      });
    };

    trySync();
    window.addEventListener('online', trySync);
    return () => {
      cancelled = true;
      window.removeEventListener('online', trySync);
    };
  }, [rideData?.pendingSync, rideData?.localDraftId, user, navigate, videoPreviewUrl, videoFile, rideData?.telemetryData]);

  const performDeleteRun = useCallback(async () => {
    if (!rideId || !user) return;

    const finishDeleteNavigation = (runDeletedMessage) => {
      removeRunIdFromUserRunListCaches(user.uid, rideId);
      navigate('/my-rides', {
        replace: true,
        state: typeof runDeletedMessage === 'string' && runDeletedMessage.trim()
          ? { runDeletedMessage: runDeletedMessage.trim() }
          : {},
      });
    };

    if (typeof rideId === 'string' && rideId.startsWith('local-')) {
      try {
        await discardLocalPendingRun(rideId.slice('local-'.length));
        finishDeleteNavigation('Run removed from this device.');
      } catch (err) {
        console.error('Delete local run failed:', err);
        alert('Failed to delete run. Please try again.');
      }
      return;
    }
    if (typeof rideId === 'string' && rideId.startsWith('queued-inline-')) {
      try {
        const clientKey = rideId.slice('queued-inline-'.length);
        removePendingQueueEntryByClientKey(clientKey);
        finishDeleteNavigation('Queued run removed from this device.');
      } catch (err) {
        console.error('Delete queued run failed:', err);
        alert('Failed to delete run. Please try again.');
      }
      return;
    }
    try {
      if (auth.currentUser) {
        try {
          await auth.currentUser.getIdToken(true);
        } catch (tokenErr) {
          console.warn('Auth token before delete run:', tokenErr);
        }
      }
      const runRef = doc(db, 'runs', rideId);
      let snap;
      try {
        snap = await getDoc(runRef);
      } catch (readErr) {
        console.warn('[RideView] getDoc before delete failed; clearing list entry:', rideId, readErr?.code || readErr);
        finishDeleteNavigation('Removed from your list. (Could not verify the server copy.)');
        return;
      }
      if (snap.exists() && ownsRunFirestoreDoc(user.uid, snap.data())) {
        try {
          await deleteDoc(snap.ref);
          finishDeleteNavigation('Run deleted from the cloud and removed from your list.');
        } catch (delErr) {
          console.warn('[RideView] deleteDoc failed:', rideId, delErr?.code || delErr);
          alert('Could not delete this run on the server. Check your connection and try again.');
        }
        return;
      } else if (snap.exists()) {
        console.warn('[RideView] run doc not owned by user; clearing stale list row:', rideId);
        finishDeleteNavigation('Removed from your list. (This run was not yours to delete on the server.)');
      } else {
        console.warn('[RideView] no Firestore doc; clearing list row:', rideId);
        finishDeleteNavigation('Removed from your list.');
      }
    } catch (err) {
      console.error('Delete run failed:', err);
      alert('Something went wrong while deleting. Please try again.');
    }
  }, [rideId, user, navigate]);

  const handleDeleteRun = useCallback(() => {
    setHeaderMenuOpen(false);
    setDeleteRunModalOpen(true);
  }, []);

  const handleExportBybFile = useCallback(() => {
    try {
      const samples = rideData?.telemetryData;
      if (!Array.isArray(samples) || samples.length === 0) {
        alert('No telemetry data to export.');
        return;
      }
      const frontTravel = Number(jackRideParams?.frontTravel);
      const rearTravel = Number(jackRideParams?.rearTravel);
      const buf = encodeTelemetrySamplesToBybArrayBuffer(samples, {
        frontSuspensionTravel: Number.isFinite(frontTravel) && frontTravel > 0 ? Math.round(frontTravel) : 200,
        frontWheelTravel: Number.isFinite(frontTravel) && frontTravel > 0 ? Math.round(frontTravel) : 200,
        rearSuspensionTravel: Number.isFinite(rearTravel) && rearTravel > 0 ? Math.round(rearTravel) : 200,
        rearWheelTravel: Number.isFinite(rearTravel) && rearTravel > 0 ? Math.round(rearTravel) : 200,
      });
      const validation = validateBybArrayBufferForDownload(buf);
      if (!validation.ok) {
        throw new Error(`BYB export self-check failed: ${validation.error}`);
      }
      const base = sanitizeFilenameBase(rideData?.runName || rideData?.name || rideId || 'run');
      const filename = `${base}.byb`;
      const blob = new Blob([buf], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setHeaderMenuOpen(false);
      setHeaderMenuExportOpen(false);
    } catch (err) {
      console.error('Export BYB failed:', err);
      alert(err?.message || 'Could not export BYB file.');
    }
  }, [jackRideParams, rideData?.telemetryData, rideData?.runName, rideData?.name, rideId]);

  const handleExportBybCsvFile = useCallback(() => {
    try {
      const samples = rideData?.telemetryData;
      if (!Array.isArray(samples) || samples.length === 0) {
        alert('No telemetry data to export.');
        return;
      }
      const text = encodeTelemetrySamplesToBybCsvText(samples);
      const base = sanitizeFilenameBase(rideData?.runName || rideData?.name || rideId || 'run');
      const filename = `${base}.csv`;
      const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setHeaderMenuOpen(false);
      setHeaderMenuExportOpen(false);
    } catch (err) {
      console.error('Export CSV failed:', err);
      alert(err?.message || 'Could not export CSV file.');
    }
  }, [rideData?.telemetryData, rideData?.runName, rideData?.name, rideId]);

  // CSV parsing from Dashboard
  const parseCSV = (text) => {
    let delimiter = ',';
    const commaCount = text.split(',').length;
    const semicolonCount = text.split(';').length;
    if (semicolonCount > commaCount) delimiter = ';';

    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(delimiter).map(h => h.trim());
    const isBYB = headers.includes('Fork') && headers.includes('Shock') &&
                  headers.includes('Fbrake') && headers.includes('Rbrake');
    const isDAQ = headers.includes('front_disp') && headers.includes('rear_disp') &&
                  headers.includes('gps_long') && headers.includes('gps_lat') && headers.includes('gps_speed');
    const isBrakeAce = headers.includes('timestamp_ms') && headers.includes('latitude') && headers.includes('longitude');

    let headerMap = {};
    if (isBrakeAce) {
      headerMap = {
        'timestamp_ms': 'time', 'latitude': 'lat', 'longitude': 'lon',
        'elevation_m': 'elevation', 'speed_m_s': 'speed',
        'front_brake_power_w': 'front_brake', 'rear_brake_power_w': 'rear_brake',
        'front_brake_force': 'front_brake_force', 'rear_brake_force': 'rear_brake_force',
        'front_lever_brake_force': 'front_lever_brake_force', 'rear_lever_brake_force': 'rear_lever_brake_force'
      };
    } else if (isBYB) {
      headerMap = {
        'Time': 'time', 'Fork': 'fork', 'Shock': 'shock',
        'Fbrake': 'front_brake', 'Rbrake': 'rear_brake',
        'Speed': 'speed', 'AccX': 'accel_x', 'AccY': 'accel_y', 'AccZ': 'accel_z',
        'GyroX': 'gyro_x', 'GyroY': 'gyro_y', 'GyroZ': 'gyro_z',
        'Latitude': 'lat', 'Longitude': 'lon', 'Altitude': 'elevation', 'gpsSpeed': 'gps_speed'
      };
    } else if (isDAQ) {
      headerMap = {
        'time': 'time', 'front_disp': 'fork', 'rear_disp': 'shock',
        'gps_long': 'lon', 'gps_lat': 'lat', 'gps_speed': 'speed',
        'acc_frame_x': 'accel_x', 'acc_frame_y': 'accel_y', 'acc_frame_z': 'accel_z',
        'gyro_frame_x': 'gyro_x', 'gyro_frame_y': 'gyro_y', 'gyro_frame_z': 'gyro_z'
      };
    } else {
      headerMap = {
        'Time (s)': 'time', 'Distance (m)': 'distance',
        'Latitude (deg)': 'lat', 'Longitude (deg)': 'lon',
        'Elevation (m)': 'elevation', 'Speed (km/h)': 'speed',
        'Front Brake Power (W)': 'front_brake', 'Rear Brake Power (W)': 'rear_brake'
      };
    }

    const colMap = {};
    headers.forEach((header, index) => {
      if (headerMap[header]) colMap[headerMap[header]] = index;
    });

    const data = [];
    for (let i = 1; i < lines.length; i += 1) {
      const values = lines[i].split(delimiter);
      if (values.length < headers.length) continue;
      const row = {};
      for (const [key, idx] of Object.entries(colMap)) {
        const v = values[idx].trim();
        const num = parseFloat(v);
        row[key] = isNaN(num) ? v : num;
      }
      if (row.time !== undefined && row.lat !== undefined && row.lon !== undefined && row.speed !== undefined) {
        data.push(row);
      }
    }

    if ((isBYB || isDAQ) && data.length > 0) {
      const forkValues = data.map(r => r.fork).filter(v => typeof v === 'number');
      const shockValues = data.map(r => r.shock).filter(v => typeof v === 'number');
      const forkRange = getFiniteMinMax(forkValues);
      if (forkRange) {
        const { min: minFork, max: maxFork } = forkRange;
        data.forEach(r => {
          if (typeof r.fork === 'number' && maxFork !== minFork) {
            r.fork_normalized = (r.fork - minFork) / (maxFork - minFork);
          }
        });
      }
      const shockRange = getFiniteMinMax(shockValues);
      if (shockRange) {
        const { min: minShock, max: maxShock } = shockRange;
        data.forEach(r => {
          if (typeof r.shock === 'number' && maxShock !== minShock) {
            r.shock_normalized = (r.shock - minShock) / (maxShock - minShock);
          }
        });
      }
      // DAQ: expose gps_speed so suspension sag uses it as m/s (matches Jack / typical GPS logs)
      if (isDAQ) {
        data.forEach(r => {
          if (r.speed !== undefined) r.gps_speed = r.speed;
        });
      }
    }

    // BrakeAce export: timestamp_ms -> seconds, speed_m_s -> km/h
    if (isBrakeAce && data.length > 0) {
      data.forEach(r => {
        if (typeof r.time === 'number') r.time = r.time / 1000;
        if (typeof r.speed === 'number') r.speed = r.speed * 3.6;
      });
    }

    data.sort((a, b) => a.time - b.time);
    return data;
  };

  // Haversine distance in meters (for GPX speed computation)
  const haversineM = (lat1, lon1, lat2, lon2) => {
    const R = 6371e3;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const parseGPX = (gpxText) => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(gpxText, 'application/xml');
    const trkpts = collectGpxTrkpts(xmlDoc);
    const results = trkpts.map((trkpt) => {
      const lat = parseFloat(trkpt.getAttribute('lat'));
      const lon = parseFloat(trkpt.getAttribute('lon'));
      const eleTag = trkpt.getElementsByTagName('ele')[0];
      const elevation = eleTag ? parseFloat(eleTag.textContent) : null;
      const timeTag = trkpt.getElementsByTagName('time')[0];
      const timeMs = timeTag ? Date.parse(timeTag.textContent) : null;
      const timeSec = timeMs != null ? timeMs / 1000 : null;
      const hrTag = trkpt.getElementsByTagName('gpxtpx:hr')[0] || trkpt.getElementsByTagName('hr')[0];
      const heartRate = hrTag ? parseFloat(hrTag.textContent) : null;
      const cadTag = trkpt.getElementsByTagName('gpxtpx:cad')[0] || trkpt.getElementsByTagName('cad')[0];
      const cadence = cadTag ? parseFloat(cadTag.textContent) : null;
      let power = null;
      const extTag = trkpt.getElementsByTagName('extensions')[0];
      if (extTag) {
        const powerTag = extTag.getElementsByTagName('power')[0] || extTag.getElementsByTagName('Power')[0];
        if (powerTag) power = parseFloat(powerTag.textContent);
      }
      return { lat, lon, elevation, time: timeSec, speed: 0, heartRate, power, cadence };
    });
    if (results.length > 0 && results[0].time != null) {
      const startTime = results[0].time;
      results.forEach((r) => { if (r.time != null) r.time = r.time - startTime; });
    }
    for (let i = 1; i < results.length; i++) {
      const prev = results[i - 1];
      const curr = results[i];
      const distMeters = haversineM(prev.lat, prev.lon, curr.lat, curr.lon);
      const dt = curr.time != null && prev.time != null ? curr.time - prev.time : 0;
      if (dt > 0) curr.speed = (distMeters / dt) * 3.6;
    }
    return results;
  };

  // Permissive CSV parse for add-metrics: requires only time + at least one metric
  const parseCSVForAddMetrics = useCallback((text) => {
    let delimiter = ',';
    const commaCount = text.split(',').length;
    const semicolonCount = text.split(';').length;
    if (semicolonCount > commaCount) delimiter = ';';
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];
    const headers = lines[0].split(delimiter).map(h => h.trim());
    const headerMap = {
      'Time (s)': 'time', 'Time': 'time', 'time': 'time', 'Elapsed': 'time', 'Seconds': 'time', 'Sec': 'time',
      'timestamp_ms': 'time',
      'Distance (m)': 'distance', 'Distance': 'distance', 'Dist': 'distance',
      'Latitude (deg)': 'lat', 'Latitude': 'lat', 'latitude': 'lat', 'Lat': 'lat', 'gps_lat': 'lat',
      'Longitude (deg)': 'lon', 'Longitude': 'lon', 'longitude': 'lon', 'Lon': 'lon', 'gps_long': 'lon',
      'Elevation (m)': 'elevation', 'Elevation': 'elevation', 'elevation_m': 'elevation', 'Altitude': 'elevation', 'Ele': 'elevation',
      'Speed (km/h)': 'speed', 'Speed': 'speed', 'speed_m_s': 'speed', 'gpsSpeed': 'gps_speed', 'gps_speed': 'speed',
      'Front Brake Power (W)': 'front_brake', 'Fbrake': 'front_brake', 'Front Brake': 'front_brake',
      'Rear Brake Power (W)': 'rear_brake', 'Rbrake': 'rear_brake', 'Rear Brake': 'rear_brake',
      'Heart Rate': 'heartRate', 'HR': 'heartRate', 'HeartRate': 'heartRate', 'BPM': 'heartRate', 'heart_rate': 'heartRate',
      'Power (W)': 'power', 'Power': 'power', 'Watts': 'power',
      'Cadence (rpm)': 'cadence', 'Cadence': 'cadence', 'RPM': 'cadence',
      'Fork': 'fork', 'Shock': 'shock', 'front_disp': 'fork', 'rear_disp': 'shock',
    };
    const colMap = {};
    headers.forEach((header, index) => {
      const key = headerMap[header] || header.replace(/\s+/g, '_').replace(/[^a-z0-9_]/gi, '').toLowerCase() || `col_${index}`;
      colMap[key] = index;
    });
    const timeIdx = headers.findIndex(h => /^time|elapsed|sec(onds)?$/i.test(h.trim()));
    const timeColHeader = timeIdx >= 0 ? headers[timeIdx] : null;
    const speedColIdx = headers.findIndex(h => headerMap[h] === 'speed');
    const speedColHeader = speedColIdx >= 0 ? headers[speedColIdx] : null;
    const data = [];
    let lastTime = 0;
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(delimiter);
      const row = {};
      for (const [key, idx] of Object.entries(colMap)) {
        const v = values[idx]?.trim();
        const num = parseFloat(v);
        if (v !== undefined && v !== '' && !isNaN(num)) row[key] = num;
      }
      let t = row.time;
      if (typeof t !== 'number' || !Number.isFinite(t)) {
        t = timeIdx >= 0 ? parseFloat(values[timeIdx]) : NaN;
      }
      if (timeColHeader === 'timestamp_ms' && typeof t === 'number') t = t / 1000;
      if (!Number.isFinite(t)) t = lastTime + (i === 1 ? 0 : 1);
      row.time = t;
      lastTime = t;
      if (speedColHeader === 'speed_m_s' && typeof row.speed === 'number') row.speed *= 3.6;
      const hasMetric = Object.keys(row).some(k => k !== 'time' && typeof row[k] === 'number' && Number.isFinite(row[k]));
      if (hasMetric) data.push(row);
    }
    data.sort((a, b) => a.time - b.time);
    return data;
  }, []);

  // Parse telemetry file to data (returns parsed array, does not set state)
  const parseTelemetryToData = useCallback(async (file) => {
    if (file.name.endsWith('.csv') || file.name.endsWith('.CSV')) {
      const text = await file.text();
      const strict = parseCSV(text);
      if (strict.length > 0) return strict;
      return parseCSVForAddMetrics(text);
    }
    if (file.name.toLowerCase().endsWith('.shq')) {
      const buffer = await file.arrayBuffer();
      const text = decodeShokiqBinaryToCsv(buffer);
      if (!text) throw new Error('Could not read this SHQ file.');
      const strict = parseCSV(text);
      if (strict.length > 0) return strict;
      return parseCSVForAddMetrics(text);
    }
    if (file.name.endsWith('.gpx') || file.name.endsWith('.GPX')) {
      const text = await file.text();
      return parseGPX(text);
    }
    if (file.name.toLowerCase().endsWith('.byb')) {
      const buffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
      const decodeUtf8 = () => new TextDecoder('utf-8', { fatal: false }).decode(buffer);
      if (bybBufferLooksLikeAppCsvExport(buffer)) {
        const text = decodeUtf8();
        const strict = parseCSV(text);
        if (strict.length > 0) return strict;
        return parseCSVForAddMetrics(text);
      }
      try {
        return parseBybTelemetryV3(buffer);
      } catch (err) {
        const text = decodeUtf8();
        let parsed = parseCSV(text);
        if (parsed.length === 0) parsed = parseCSVForAddMetrics(text);
        if (parsed.length > 0) return parsed;
        throw err;
      }
    }
    if (file.name.endsWith('.fit') || file.name.endsWith('.FIT')) {
      const buffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
      return new Promise((resolve, reject) => {
        const fitParser = new FitParser({
          force: true,
          speedUnit: 'km/h',
          lengthUnit: 'km',
          temperatureUnit: 'celcius',
          elapsedRecordField: true,
        });
        fitParser.parse(buffer, (error, data) => {
          if (error) return reject(error);
          if (!data.records?.length) return reject(new Error('No records in FIT file'));
          const startTs = new Date(data.records[0].timestamp).getTime() / 1000;
          const result = data.records.map((r) => {
            const t = new Date(r.timestamp).getTime() / 1000;
            const row = {
              time: t - startTs,
              lat: r.position_lat ?? null,
              lon: r.position_long ?? null,
              speed: r.speed ?? null,
              heartRate: r.heart_rate ?? null,
              power: r.power ?? null,
              cadence: r.cadence ?? null,
              elevation: r.altitude ?? null,
            };
            const skipKeys = new Set(['timestamp', 'position_lat', 'position_long', 'speed', 'heart_rate', 'power', 'cadence', 'altitude']);
            for (const key of Object.keys(r)) {
              if (skipKeys.has(key)) continue;
              const v = r[key];
              if (typeof v === 'number' && Number.isFinite(v)) row[key] = v;
            }
            return row;
          });
          result.sort((a, b) => a.time - b.time);
          resolve(result);
        });
      });
    }
    throw new Error('Unsupported file type. Use CSV, SHQ, GPX, FIT, or BYB.');
  }, [parseCSVForAddMetrics]);

  // Parse telemetry file (CSV, FIT, GPX, or BYB) - sets state
  const parseTelemetryFile = useCallback(async (file) => {
    const hasVideoSource = !!(videoPreviewUrl || rideData?.videoUrl || rideData?.youtubeUrl);
    const computeDuration = (arr) => {
      if (!arr?.length) return 0;
      const times = arr.map(d => d.time ?? 0).filter(t => typeof t === 'number' && Number.isFinite(t));
      const range = getFiniteMinMax(times);
      if (!range) return 0;
      const { min: minT, max: maxT } = range;
      return minT > 60 ? Math.max(0, maxT - minT) : maxT;
    };
    if (file.name.endsWith('.csv') || file.name.endsWith('.CSV')) {
      const text = await file.text();
      let parsed = parseCSV(text);
      if (parsed.length === 0) parsed = parseCSVForAddMetrics(text);
      setRideData((prev) => ({ ...(prev || {}), telemetryData: parsed }));
      if (!hasVideoSource && parsed.length > 0) {
        const dur = computeDuration(parsed);
        if (dur > 0) setDuration(dur);
      }
    } else if (file.name.toLowerCase().endsWith('.shq')) {
      const buffer = await file.arrayBuffer();
      const text = decodeShokiqBinaryToCsv(buffer);
      if (!text) {
        alert('Could not read this SHQ file.');
        return;
      }
      let parsed = parseCSV(text);
      if (parsed.length === 0) parsed = parseCSVForAddMetrics(text);
      if (!parsed?.length) {
        alert('No telemetry samples found in this SHQ file.');
        return;
      }
      setRideData((prev) => ({ ...(prev || {}), telemetryData: parsed }));
      if (!hasVideoSource && parsed.length > 0) {
        const dur = computeDuration(parsed);
        if (dur > 0) setDuration(dur);
      }
    } else if (file.name.endsWith('.gpx') || file.name.endsWith('.GPX')) {
      const text = await file.text();
      const parsed = parseGPX(text);
      if (!parsed?.length) {
        alert('No track points found in this GPX file. Try an activity/track export (not route-only).');
        return;
      }
      setRideData((prev) => ({ ...(prev || {}), telemetryData: parsed }));
      if (!hasVideoSource && parsed.length > 0) {
        const dur = computeDuration(parsed);
        if (dur > 0) setDuration(dur);
      }
    } else if (file.name.toLowerCase().endsWith('.byb')) {
      const buffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
      const decodeUtf8 = () => new TextDecoder('utf-8', { fatal: false }).decode(buffer);
      let parsed;
      if (bybBufferLooksLikeAppCsvExport(buffer)) {
        const text = decodeUtf8();
        parsed = parseCSV(text);
        if (parsed.length === 0) parsed = parseCSVForAddMetrics(text);
      } else {
        try {
          parsed = parseBybTelemetryV3(buffer);
        } catch (err) {
          console.warn('[RideView] BYB binary decode failed, trying CSV:', err?.message || err);
          const text = decodeUtf8();
          parsed = parseCSV(text);
          if (parsed.length === 0) parsed = parseCSVForAddMetrics(text);
          if (!parsed?.length) {
            console.error('BYB parse error:', err);
            alert(err?.message || 'Could not read this BYB file.');
            return;
          }
        }
      }
      if (!parsed?.length) {
        alert('No telemetry samples found in this BYB file.');
        return;
      }
      setRideData((prev) => ({ ...(prev || {}), telemetryData: parsed }));
      if (!hasVideoSource && parsed.length > 0) {
        const dur = computeDuration(parsed);
        if (dur > 0) setDuration(dur);
      }
    } else if (file.name.endsWith('.fit') || file.name.endsWith('.FIT')) {
      const buffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
      const fitParser = new FitParser({
        force: true,
        speedUnit: 'km/h',
        lengthUnit: 'km',
        temperatureUnit: 'celcius',
        elapsedRecordField: true,
      });
      fitParser.parse(buffer, (error, data) => {
        if (error) {
          console.error('FIT parse error:', error);
          return;
        }
        if (!data.records || data.records.length === 0) {
          console.error('No records found in FIT file');
          return;
        }
        const startTs = new Date(data.records[0].timestamp).getTime() / 1000;
        const result = data.records.map((r) => {
          const t = new Date(r.timestamp).getTime() / 1000;
          const row = {
            time: t - startTs,
            lat: r.position_lat ?? null,
            lon: r.position_long ?? null,
            speed: r.speed ?? null,
            heartRate: r.heart_rate ?? null,
            power: r.power ?? null,
            cadence: r.cadence ?? null,
            elevation: r.altitude ?? null,
          };
          const skipKeys = new Set(['timestamp', 'position_lat', 'position_long', 'speed', 'heart_rate', 'power', 'cadence', 'altitude']);
          for (const key of Object.keys(r)) {
            if (skipKeys.has(key)) continue;
            const v = r[key];
            if (typeof v === 'number' && Number.isFinite(v)) row[key] = v;
          }
          return row;
        });
        result.sort((a, b) => a.time - b.time);
        setRideData((prev) => ({ ...(prev || {}), telemetryData: result }));
        if (!hasVideoSource && result.length > 0) {
          const dur = computeDuration(result);
          if (dur > 0) setDuration(dur);
        }
      });
    }
  }, [rideData?.videoUrl, rideData?.youtubeUrl, videoPreviewUrl, parseCSVForAddMetrics]);

  // Handle add metrics from file - parse, detect conflicts, merge
  const handleAddMetrics = useCallback(async (file) => {
    if (!file) return;
    const current = rideData?.telemetryData;
    if (!current?.length) {
      try {
        await parseTelemetryFile(file);
      } catch (err) {
        console.error('Add metrics (no existing data):', err);
        alert(err?.message || 'Failed to load file.');
      }
      return;
    }
    try {
      const newData = await parseTelemetryToData(file);
      if (!newData?.length) {
        alert('No valid data found in the file.');
        return;
      }
      const currentKeys = new Set();
      const newKeys = new Set();
      const allRowKeys = new Set();
      current.forEach((d) => Object.keys(d || {}).forEach((k) => allRowKeys.add(k)));
      newData.forEach((d) => Object.keys(d || {}).forEach((k) => allRowKeys.add(k)));
      allRowKeys.forEach((k) => {
        if (k === 'time') return;
        const curHas = current.some((d) => typeof d[k] === 'number' && Number.isFinite(d[k]));
        const newHas = newData.some((d) => typeof d[k] === 'number' && Number.isFinite(d[k]));
        if (curHas) currentKeys.add(k);
        if (newHas) newKeys.add(k);
      });
      const hasGpsNew = newKeys.has('lat') || newKeys.has('lon');
      const hasGpsCurrent = currentKeys.has('lat') || currentKeys.has('lon');
      const allNewMetrics = [...newKeys].filter((k) => k !== 'time' && k !== 'lat' && k !== 'lon');
      if (hasGpsNew) allNewMetrics.push('gps');
      const conflicts = new Set(allNewMetrics.filter((k) => {
        if (k === 'gps') return hasGpsCurrent && hasGpsNew;
        return currentKeys.has(k) || (k === 'heart_rate' && currentKeys.has('heartRate')) || (k === 'heartRate' && currentKeys.has('heart_rate'));
      }));
      const defaultChoices = {};
      allNewMetrics.forEach((k) => { defaultChoices[k] = conflicts.has(k) ? 'keep' : 'import'; });
      if (conflicts.has('heartRate') || conflicts.has('heart_rate')) defaultChoices.heartRate = defaultChoices.heart_rate = 'keep';
      if (allNewMetrics.length === 0) {
        alert('No metrics found in the new file to add.');
        return;
      }
      setAddMetricsModal({
        newData,
        allNewMetrics,
        conflicts: new Set(conflicts),
        choices: defaultChoices,
      });
    } catch (err) {
      console.error('Add metrics error:', err);
      alert(err.message || 'Failed to parse file.');
    }
  }, [rideData?.telemetryData, parseTelemetryToData, parseTelemetryFile]);

  const handleSaveExistingRide = useCallback(async () => {
    if (!rideId || !rideData || rideId === 'create' || rideId === 'new') return;
    if (typeof rideId === 'string' && rideId.startsWith('local-')) {
      const localDraftId = rideId.slice('local-'.length);
      setSaving(true);
      try {
        const td = rideData.telemetryData || [];
        let analysisTrim = rideData.analysisTrim;
        if (Array.isArray(td) && td.length > 0) {
          const { minT, maxT } = getTelemetryTimeBounds(td);
          const start = rideData.analysisTrim?.startTime != null ? rideData.analysisTrim.startTime : minT;
          const end = rideData.analysisTrim?.endTime != null ? rideData.analysisTrim.endTime : maxT;
          analysisTrim = { startTime: start, endTime: end };
        }
        const partial = serializeRunForOfflineDraft({
          telemetryData: td,
          videoOffset,
          dataOffset,
          analysisTrim,
        });
        const ok = await updatePendingRunDraft(localDraftId, partial);
        if (!ok) throw new Error('Local draft not found.');
        setHasUnsavedChanges(false);
      } catch (err) {
        console.error('Failed to save local ride:', err);
        alert(err?.message || 'Failed to save changes.');
      } finally {
        setSaving(false);
      }
      return;
    }
    if (typeof rideId === 'string' && rideId.startsWith('queued-inline-')) {
      alert('This run is still in your offline upload queue. After it syncs to the cloud you can save edits there.');
      return;
    }
    setSaving(true);
    try {
      const update = {
        telemetryData: rideData.telemetryData || [],
        updatedAt: serverTimestamp(),
      };
      if (typeof videoOffset === 'number') update.videoOffset = videoOffset;
      if (typeof dataOffset === 'number') update.dataOffset = dataOffset;
      if (Array.isArray(rideData.telemetryData) && rideData.telemetryData.length > 0) {
        const { minT, maxT } = getTelemetryTimeBounds(rideData.telemetryData);
        const start = rideData.analysisTrim?.startTime != null ? rideData.analysisTrim.startTime : minT;
        const end = rideData.analysisTrim?.endTime != null ? rideData.analysisTrim.endTime : maxT;
        update.analysisTrim = { startTime: start, endTime: end };
      }
      await updateDoc(doc(db, 'runs', rideId), update);
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Failed to save ride:', err);
      alert(err?.message || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  }, [rideId, rideData, videoOffset, dataOffset]);

  const applyAddMetricsMerge = useCallback(() => {
    if (!addMetricsModal) return;
    const { newData, choices } = addMetricsModal;
    const current = rideData?.telemetryData;
    if (!current?.length) return;
    const merged = mergeTelemetryByTime(current, newData, choices);
    setRideData((prev) => ({ ...prev, telemetryData: merged }));
    const hasVideoSource = !!(videoPreviewUrl || rideData?.videoUrl || rideData?.youtubeUrl);
    if (!hasVideoSource) {
      const times = merged.map((d) => d.time ?? 0).filter(t => typeof t === 'number' && Number.isFinite(t));
      const range = getFiniteMinMax(times);
      if (range) {
        const { min: minT, max: maxT } = range;
        const dur = minT > 60 ? Math.max(0, maxT - minT) : maxT;
        if (dur > 0) setDuration(dur);
      }
    }
    setAddMetricsModal(null);
    setHasUnsavedChanges(true);
  }, [addMetricsModal, rideData?.telemetryData]);

  const handleSaveAiInsight = useCallback(
    async (insightText) => {
      const text = typeof insightText === 'string' ? insightText.trim() : '';
      if (!text || !rideId) return;
      const savedAt = Date.now();
      const patch = {
        suspensionAiInsight: text,
        suspensionAiInsightUpdatedAt: savedAt,
      };
      setRideData((prev) => (prev ? { ...prev, ...patch } : prev));
      try {
        if (typeof rideId === 'string' && rideId.startsWith('local-')) {
          const localDraftId = rideId.slice('local-'.length);
          await updatePendingRunDraft(localDraftId, serializeRunForOfflineDraft(patch));
          return;
        }
        if (typeof rideId === 'string' && rideId.startsWith('queued-inline-')) {
          const clientKey = rideId.slice('queued-inline-'.length);
          updatePendingInlineRunByClientKey(clientKey, patch);
          return;
        }
        await updateDoc(doc(db, 'runs', rideId), {
          ...patch,
          updatedAt: serverTimestamp(),
        });
      } catch (err) {
        console.error('Failed to persist AI insight:', err);
      }
    },
    [rideId],
  );

  const finalizeViewRunName = useCallback(async () => {
    if (!rideData || isCreationMode) return;
    const original = String(rideData.runName || rideData.name || '').trim();
    const next = String(runName || '').trim();
    if (next === original) return;
    if (!next) {
      setRunName(original);
      return;
    }

    const isLocal = typeof rideId === 'string' && rideId.startsWith('local-');
    const isQueued = typeof rideId === 'string' && rideId.startsWith('queued-inline-');
    const isCloud =
      typeof rideId === 'string' && rideId.length > 0 && !isLocal && !isQueued;

    if (isCloud && user?.uid && !ownsRunFirestoreDoc(user.uid, rideData)) {
      alert('Only the run owner can rename.');
      setRunName(original);
      return;
    }

    const merged = { ...rideData, runName: next, name: next };
    setRideData(merged);
    if (user?.uid) {
      patchRunMetadataInUserRunListCaches(user.uid, rideId, { runName: next, name: next });
    }

    const persistOfflineMetaCache = () => {
      if (user?.uid && isCloud) {
        saveOfflineRunDetailCache(user.uid, rideId, merged).catch((err) =>
          console.warn('[RideView] saveOfflineRunDetailCache after rename:', err),
        );
      }
    };

    try {
      if (isLocal) {
        const localDraftId = rideId.slice('local-'.length);
        const ok = await updatePendingRunDraft(
          localDraftId,
          serializeRunForOfflineDraft({ runName: next, name: next }),
        );
        if (!ok) console.warn('[RideView] rename: local draft update failed');
        persistOfflineMetaCache();
        return;
      }
      if (isQueued) {
        const clientKey = rideId.slice('queued-inline-'.length);
        updatePendingInlineRunByClientKey(clientKey, { runName: next, name: next });
        persistOfflineMetaCache();
        return;
      }
      if (isCloud) {
        const offline = typeof navigator !== 'undefined' && !navigator.onLine;
        if (offline) {
          enqueuePendingRunMetadataUpdate(rideId, { runName: next, name: next });
          persistOfflineMetaCache();
          return;
        }
        try {
          await updateDoc(doc(db, 'runs', rideId), {
            runName: next,
            name: next,
            updatedAt: serverTimestamp(),
          });
          clearPendingRunMetadataUpdateForRun(rideId);
          persistOfflineMetaCache();
        } catch (err) {
          console.warn('[RideView] rename updateDoc failed, queueing for retry:', err);
          enqueuePendingRunMetadataUpdate(rideId, { runName: next, name: next });
          persistOfflineMetaCache();
        }
      }
    } catch (err) {
      console.error('[RideView] finalizeViewRunName:', err);
      alert(err?.message || 'Could not save run name.');
      setRideData((prev) => (prev ? { ...prev, runName: original, name: original } : prev));
      setRunName(original);
    }
  }, [rideData, isCreationMode, runName, rideId, user]);

  // Handle video drop in creation mode
  const handleVideoDrop = useCallback(async (e) => {
    if (typeof e?.preventDefault === 'function') e.preventDefault();
    const files = Array.from(e.dataTransfer?.files || []);
    const videoFile = files.find(f => f.type.startsWith('video/'));
    if (videoFile) {
      setVideoFile(videoFile);
      setVideoPreviewUrl(URL.createObjectURL(videoFile));
      
      // Start GPS extraction immediately if we don't have telemetry data yet
      // Pass File directly - gpmf-extract streams in 2MB chunks, no full-file load (works for 4GB+)
      if (!rideData?.telemetryData || rideData.telemetryData.length === 0) {
        const fileSizeMB = videoFile.size / 1024 / 1024;
        const startTime = Date.now();
        const goproAbortController = new AbortController();
        uploadStatus.startGoproExtraction('extracting', goproAbortController);

        setTimeout(async () => {
          try {
            uploadStatus.updateGoproExtraction(5);
            const { extractGoProGPS: extractGPSUtil, validateGPSData } = await import('../utils/goproExtractor');

            if (goproAbortController?.signal.aborted) {
              throw new Error('Extraction cancelled');
            }

            const extractionResult = await extractGPSUtil(videoFile, {
              requireGpsFix: false,
              debug: false,
              maxRetries: 3
            }, (progress, message) => {
              if (goproAbortController?.signal.aborted) {
                return;
              }
              uploadStatus.updateGoproExtraction(progress);
              const elapsed = (Date.now() - startTime) / 1000;
              const estimatedTotal = Math.max(10, (fileSizeMB * 0.5)); // ~0.5s per MB for streaming
              const remaining = Math.max(0, estimatedTotal - elapsed);
              uploadStatus.updateGoproExtraction(progress, remaining);
            });

            uploadStatus.updateGoproExtraction(90);

            if (extractionResult && extractionResult.success) {
              const validation = validateGPSData(extractionResult.data);
              if (validation && validation.valid) {
                console.log('✅ GPS data extracted from video in create mode');
                // Use parseCSV to normalize headers (Time (s)->time, Latitude (deg)->lat, etc)
                const parsedData = parseCSV(extractionResult.data);
                if (parsedData && parsedData.length > 0) {
                  setRideData(prev => ({
                    ...prev,
                    telemetryData: parsedData,
                  }));
                  // Don't overwrite duration when we have video — video is source of truth
                  const hasVideoSource = !!videoPreviewUrl;
                  if (!hasVideoSource) {
                    const times = parsedData.map(d => d.time ?? 0).filter(t => typeof t === 'number' && Number.isFinite(t));
                    const range = getFiniteMinMax(times);
                    if (range) {
                      const { min: minT, max: maxT } = range;
                      const dur = minT > 60 ? Math.max(0, maxT - minT) : maxT;
                      if (dur > 0) setDuration(dur);
                    }
                  }
                  uploadStatus.updateGoproExtraction(100);
                } else {
                  uploadStatus.updateGoproExtraction(0);
                  uploadStatus.completeGoproExtraction();
                }
              } else {
                uploadStatus.updateGoproExtraction(0);
                uploadStatus.completeGoproExtraction();
              }
            } else {
              uploadStatus.updateGoproExtraction(0);
              uploadStatus.completeGoproExtraction();
            }
            // Only complete if not already completed above
            if (uploadStatus.goproExtraction.active) {
              uploadStatus.completeGoproExtraction();
            }
          } catch (error) {
            if (error.name === 'AbortError' || error.message === 'Extraction cancelled') {
              console.log('GPS extraction cancelled by user');
            } else {
              console.error('Error extracting GPS from video:', error);
            }
            if (uploadStatus.goproExtraction.active) {
              uploadStatus.completeGoproExtraction();
            }
          }
        }, 0);
      }
    }
  }, [rideData, uploadStatus]);

  // Handle telemetry drop in creation mode
  const handleTelemetryDrop = useCallback((e) => {
    if (typeof e?.preventDefault === 'function') e.preventDefault();
    const files = Array.from(e.dataTransfer?.files || []);
    const telemetryFile = files.find(isTelemetryDataFile);
    if (telemetryFile) {
      setTelemetryFile(telemetryFile);
      Promise.resolve(parseTelemetryFile(telemetryFile)).catch((err) => {
        console.error('Telemetry load error:', err);
        alert(err?.message || 'Failed to load data file.');
      });
    }
  }, [parseTelemetryFile]);

  // Handle remove video in creation mode
  const handleRemoveVideo = useCallback(() => {
    if (videoPreviewUrl) {
      URL.revokeObjectURL(videoPreviewUrl);
    }
    setVideoFile(null);
    setVideoPreviewUrl(null);
    setRideData((prev) => (prev && prev.localVideoUrl ? { ...prev, localVideoUrl: null } : prev));
  }, [videoPreviewUrl]);

  // View mode: when run has local/offline video only, user can drop or select file to play
  const handleLocalVideoDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer?.files || []);
    const file = files.find((f) => f.type.startsWith('video/'));
    if (file) {
      if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
      setVideoFile(file);
      setVideoPreviewUrl(URL.createObjectURL(file));
    }
  }, [videoPreviewUrl]);

  const handleLocalVideoFileInput = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('video/')) {
      if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
      setVideoFile(file);
      setVideoPreviewUrl(URL.createObjectURL(file));
    }
    e.target.value = '';
  }, [videoPreviewUrl]);

  // Handle remove telemetry data in creation mode
  const handleRemoveTelemetry = useCallback(() => {
    setTelemetryFile(null);
    setRideData((prev) => ({
      ...(prev || {}),
      telemetryData: [],
    }));
    const hasVideoSource = !!(videoPreviewUrl || rideData?.videoUrl || rideData?.youtubeUrl);
    if (!hasVideoSource) setDuration(0);
  }, [videoPreviewUrl, rideData?.videoUrl, rideData?.youtubeUrl]);

  // Handle import from Strava/BrakeAce
  const handleImportFromSource = useCallback(({ runName: importedName, telemetryData: importedTelemetry, startDate: importedStartDate }) => {
    if (importedName) setRunName(importedName);
    if (Array.isArray(importedTelemetry) && importedTelemetry.length > 0) {
      setRideData(prev => ({
        ...(prev || {}),
        telemetryData: importedTelemetry,
        ...(importedStartDate != null ? { strava: { ...(prev?.strava || {}), startDate: importedStartDate } } : {}),
      }));
      const hasVideoSource = !!(videoPreviewUrl || rideData?.videoUrl || rideData?.youtubeUrl);
      if (!hasVideoSource) {
        const times = importedTelemetry.map(d => d.time ?? 0).filter(t => typeof t === 'number' && Number.isFinite(t));
        const range = getFiniteMinMax(times);
        if (range) {
          const { min: minT, max: maxT } = range;
          const dur = minT > 60 ? Math.max(0, maxT - minT) : maxT;
          if (dur > 0) setDuration(dur);
        }
      }
    } else if (importedStartDate != null) {
      setRideData(prev => ({
        ...(prev || {}),
        strava: { ...(prev?.strava || {}), startDate: importedStartDate },
      }));
    }
    if (importedStartDate) {
      const d = typeof importedStartDate === 'string' ? new Date(importedStartDate) : importedStartDate;
      setImportedActivityDate(d && !Number.isNaN(d.getTime()) ? d : null);
    } else {
      setImportedActivityDate(null);
    }
  }, [rideData?.videoUrl, rideData?.youtubeUrl, videoPreviewUrl]);

  // Unified drop handler - accepts both video and data files, routes to appropriate handler
  const handleUnifiedDrop = useCallback(async (e) => {
    if (typeof e?.preventDefault === 'function') e.preventDefault();
    if (typeof e?.stopPropagation === 'function') e.stopPropagation();
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;

    const videoFile = files.find((f) => f.type.startsWith('video/'));
    if (videoFile) {
      await handleVideoDrop(e);
      return;
    }

    const telemetryFile = files.find(isTelemetryDataFile);
    if (telemetryFile) {
      handleTelemetryDrop({
        preventDefault: () => {},
        stopPropagation: () => {},
        dataTransfer: { files: [telemetryFile] },
      });
    }
  }, [handleVideoDrop, handleTelemetryDrop]);

  // Open file browser when clicking drop zone (creation mode)
  const handleUnifiedFileInput = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    const syntheticEvent = {
      preventDefault: () => {},
      stopPropagation: () => {},
      dataTransfer: { files },
    };
    handleUnifiedDrop(syntheticEvent);
  }, [handleUnifiedDrop]);

  const triggerUnifiedFileBrowse = useCallback(() => {
    unifiedFileInputRef.current?.click();
  }, []);

  // Actual save: optionally upload video to cloud (YouTube). Called after modal choice or when no video.
  const doSaveRun = useCallback(async (uploadToCloud) => {
    if (!user) return;
    setSaving(true);
    uploadStatus.startSaveRun('Saving run...');

    try {
      let performedDate = importedActivityDate && !Number.isNaN(importedActivityDate.getTime()) ? importedActivityDate : null;
      if (!performedDate && rideData?.strava?.startDate) {
        const d = typeof rideData.strava.startDate === 'string' ? new Date(rideData.strava.startDate) : rideData.strava.startDate;
        if (d && !Number.isNaN(d.getTime())) performedDate = d;
      }
      const now = Date.now();
      const timestamp = performedDate ? Timestamp.fromDate(performedDate) : now;

      const telemetryDataRaw = Array.isArray(rideData?.telemetryData) ? rideData.telemetryData : [];
      // Keep full telemetry in the IndexedDB draft until cloud sync runs `tryOffloadRunTelemetryToStorage`.
      // Pre-offloading here left drafts with [] rows so `tryUploadPendingDraftOnce` saved an empty IndexedDB snapshot.
      if (telemetryDataRaw.length > 0) {
        try {
          const approx = JSON.stringify(telemetryDataRaw);
          if (typeof approx === 'string' && approx.length > TELEMETRY_OFFLOAD_JSON_LEN) {
            console.warn(
              'Telemetry is large; it will be uploaded to Storage during cloud sync. If sync fails, check Storage rules.',
            );
          }
        } catch {
          // ignore size probe errors
        }
      }

      const serialized = serializeRunForOfflineDraft({
        userId: user.uid,
        ownerId: user.uid,
        runName: runName || `Run ${new Date().toLocaleDateString()}`,
        name: runName || `Run ${new Date().toLocaleDateString()}`,
        description: description || '',
        videoUrl: null,
        videoFileName: videoFile?.name || null,
        telemetryData: telemetryDataRaw,
        timestamp,
        ...(performedDate ? { date: Timestamp.fromDate(performedDate) } : {}),
        ...(rideData?.strava?.startDate != null ? { strava: { startDate: rideData.strava.startDate } } : {}),
        ...(videoFile && !uploadToCloud ? { uploadCancelled: true, uploadedToYouTube: false } : {}),
        ...(typeof videoOffset === 'number' && videoOffset !== 0 ? { videoOffset } : {}),
        ...(typeof dataOffset === 'number' && dataOffset !== 0 ? { dataOffset } : {}),
      });

      let localDraftId;
      try {
        localDraftId = await enqueuePendingRunDraft(serialized);
      } catch (queueErr) {
        console.error('Failed to save run locally:', queueErr);
        alert('Failed to save run: ' + (queueErr?.message || 'Could not store run on device.'));
        setSaving(false);
        uploadStatus.completeSaveRun();
        return;
      }

      uploadStatus.completeSaveRun();
      setSaving(false);

      const navState = {
        localVideoUrl: videoPreviewUrl,
        videoFile: videoFile,
        telemetryData: rideData?.telemetryData || [],
      };

      navigate(`/ride/local-${localDraftId}`, { state: navState });

      const startPostCloudJobs = (firestoreDocId) => {
        if (videoFile && uploadToCloud) {
          const abortController = new AbortController();
          uploadStatus.startYoutubeUpload(firestoreDocId, videoFile.name, abortController);
          setTimeout(() => {
            uploadToYouTube(videoFile, firestoreDocId, runName, abortController);
          }, 100);
        }

        if (videoFile && (!rideData?.telemetryData || rideData.telemetryData.length === 0)) {
          console.log('Starting background GoPro GPS extraction...');
          const goproAbortController = new AbortController();
          uploadStatus.startGoproExtraction(firestoreDocId, goproAbortController);
          setTimeout(() => {
            extractGoProGPS(videoFile, goproAbortController).then((extractedData) => {
              if (extractedData && extractedData.length > 0) {
                console.log('Updating Firestore with extracted GPS data');
                const runRef = doc(db, 'runs', firestoreDocId);
                updateDoc(runRef, { telemetryData: extractedData });
              }
              uploadStatus.completeGoproExtraction();
            }).catch((error) => {
              if (error.name === 'AbortError' || error.message === 'Extraction cancelled') {
                console.log('GPS extraction cancelled by user');
              } else {
                console.error('Error in GPS extraction:', error);
              }
              uploadStatus.completeGoproExtraction();
            });
          }, 100);
        }
      };

      if (navigator.onLine) {
        uploadStatus.startCloudSyncRun('Syncing to cloud...');
        const cloudSyncStatusMs = 18000;
        const statusTimer = setTimeout(() => {
          uploadStatus.completeCloudSyncRun();
        }, cloudSyncStatusMs);

        tryUploadPendingDraftOnce(db, user, localDraftId)
          .then((firestoreId) => {
            clearTimeout(statusTimer);
            uploadStatus.completeCloudSyncRun();
            if (typeof firestoreId === 'string' && firestoreId.length > 0) {
              try {
                const runForUi = deserializeRunFromOfflineDraft(serialized);
                patchCachesAfterPendingRunSynced(user.uid, localDraftId, firestoreId, runForUi);
              } catch (e) {
                console.warn('Cache patch after sync skipped:', e);
              }
              navigate(`/ride/${firestoreId}`, { replace: true, state: navState });
              startPostCloudJobs(firestoreId);
            }
          })
          .catch((err) => {
            clearTimeout(statusTimer);
            uploadStatus.completeCloudSyncRun();
            console.warn('Cloud sync failed; will retry when online:', err);
          });
      }

    } catch (error) {
      console.error('Error saving run:', error);
      alert('Failed to save run: ' + (error?.message || ''));
      setSaving(false);
      uploadStatus.completeSaveRun();
      uploadStatus.completeCloudSyncRun();
    }
  }, [user, runName, description, videoFile, videoPreviewUrl, rideData?.telemetryData, rideData?.strava, importedActivityDate, navigate, uploadStatus, videoOffset, dataOffset]);

  // Handle save run in creation mode: show video choice modal if there is a video, else save directly
  const handleSaveRun = useCallback(() => {
    if (!user) {
      alert('User not authenticated');
      return;
    }
    if (videoFile) {
      setSaveVideoModalStep('choice');
    } else {
      doSaveRun(true);
    }
  }, [user, videoFile, doSaveRun]);

  // YouTube access token helper
  const getAccessToken = async () => {
    try {
      const response = await fetch("https://oauth2.googleapis.com/token", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          client_id: CLIENT_ID,
          client_secret: CLIENT_SECRET,
          refresh_token: REFRESH_TOKEN,
          grant_type: "refresh_token",
        }),
      });
      const data = await response.json();
      if (data.access_token) {
        return data.access_token;
      } else {
        throw new Error(`Failed to get access token: ${JSON.stringify(data)}`);
      }
    } catch (error) {
      console.error("❌ Error getting access token:", error);
      return null;
    }
  };

  // Extract GoPro GPS data from video in browser
  const extractGoProGPS = useCallback(async (file, abortController) => {
    if (!file) {
      console.log("No video file provided for GPS extraction");
      return null;
    }

    const startTime = Date.now();
    uploadStatus.updateGoproExtraction(10);

    try {
      const reader = new FileReader();
      const arrayBuffer = await new Promise((resolve, reject) => {
        // Check for cancellation before reading
        if (abortController?.signal.aborted) {
          reject(new Error('Extraction cancelled'));
          return;
        }

        reader.onload = (e) => {
          if (abortController?.signal.aborted) {
            reject(new Error('Extraction cancelled'));
            return;
          }
          resolve(e.target.result);
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });

      uploadStatus.updateGoproExtraction(30);

      // Import GoPro extraction utility
      const { extractGoProGPS, validateGPSData } = await import('../utils/goproExtractor');

      uploadStatus.updateGoproExtraction(50);

      // Check for cancellation before extraction
      if (abortController?.signal.aborted) {
        throw new Error('Extraction cancelled');
      }

      // Extract GPS data
      const extractionResult = await extractGoProGPS(arrayBuffer, {
        requireGpsFix: false,
        debug: false,
        maxRetries: 3
      }, (progress, message) => {
        // Check for cancellation during extraction
        if (abortController?.signal.aborted) {
          return;
        }

        const totalProgress = 50 + (progress * 0.3);
        uploadStatus.updateGoproExtraction(totalProgress);
        
        // Calculate time estimate (rough estimate: 2 seconds per MB)
        const elapsed = (Date.now() - startTime) / 1000;
        const fileSizeMB = file.size / 1024 / 1024;
        const estimatedTotal = Math.max(5, fileSizeMB * 2);
        const remaining = Math.max(0, estimatedTotal - elapsed);
        uploadStatus.updateGoproExtraction(totalProgress, remaining);
      });

      uploadStatus.updateGoproExtraction(90);

      if (extractionResult.success) {
        const validation = validateGPSData(extractionResult.data);
        if (validation.valid) {
          console.log("✅ GoPro GPS extracted successfully:", extractionResult.method);
          uploadStatus.updateGoproExtraction(100);
          // Return parsed telemetry data
          return parseCSV(extractionResult.data);
        }
      }
      
      uploadStatus.updateGoproExtraction(0);
      console.log("⚠️ No valid GoPro GPS data found");
      return null;
    } catch (error) {
      if (error.message === 'Extraction cancelled' || abortController?.signal.aborted) {
        console.log("✅ GPS extraction cancelled by user");
        uploadStatus.updateGoproExtraction(0);
        return null;
      }
      console.error("❌ Error extracting GoPro GPS:", error);
      uploadStatus.updateGoproExtraction(0);
      return null;
    }
  }, [uploadStatus]);

  // Upload video to YouTube in background
  const uploadToYouTube = useCallback(async (file, runId, runName, abortController) => {
    if (!file || !runId) {
      console.log("Missing file or runId for YouTube upload");
      uploadStatus.completeYoutubeUpload();
      return;
    }

    const startTime = Date.now();
    const fileSizeMB = file.size / 1024 / 1024;

    try {
      const token = await getAccessToken();
      if (!token) {
        console.error("❌ Error: No YouTube access token.");
        uploadStatus.completeYoutubeUpload();
        return;
      }

      const metadata = {
        snippet: {
          title: runName || "New Run",
          description: "Uploaded from Victorise.",
          tags: ["cycling", "telemetry"],
          categoryId: "22",
        },
        status: { privacyStatus: "unlisted" },
      };

      // Step 1: Initiate resumable upload
      const initiateResumableUpload = async () => {
        const response = await fetch(
          "https://www.googleapis.com/upload/youtube/v3/videos?uploadType=resumable&part=snippet,status",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json; charset=UTF-8",
              "X-Upload-Content-Length": file.size,
              "X-Upload-Content-Type": file.type,
            },
            body: JSON.stringify(metadata),
            signal: abortController?.signal,
          }
        );

        if (!response.ok) {
          throw new Error("❌ Failed to initiate resumable upload");
        }
        return response.headers.get("location");
      };

      let uploadUrl = await initiateResumableUpload();
      let offset = 0;
      const chunkSize = 10 * 1024 * 1024; // 10MB

      // Step 2: Check upload status and resume if needed
      const getUploadStatus = async () => {
        const statusResponse = await fetch(uploadUrl, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Range": `bytes */${file.size}`,
          },
          signal: abortController?.signal,
        });

        if (statusResponse.status === 308) {
          const rangeHeader = statusResponse.headers.get("Range");
          if (rangeHeader) {
            const rangeMatch = rangeHeader.match(/bytes=(\d+)-(\d+)/);
            if (rangeMatch) {
              offset = parseInt(rangeMatch[2]) + 1;
              console.log(`📌 Resuming upload from byte ${offset}`);
            }
          }
        }
      };

      await getUploadStatus();

      // Step 3: Upload chunks
      while (offset < file.size) {
        // Check if upload was cancelled
        if (abortController?.signal.aborted) {
          console.log('Upload cancelled by user');
          throw new Error('Upload cancelled');
        }

        const chunk = file.slice(offset, offset + chunkSize);

        const response = await fetch(uploadUrl, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Length": chunk.size,
            "Content-Range": `bytes ${offset}-${offset + chunk.size - 1}/${file.size}`,
          },
          body: chunk,
          signal: abortController?.signal,
        });

        if (!response.ok && response.status !== 308) {
          throw new Error(`❌ Chunk upload failed: ${await response.text()}`);
        }

        offset += chunk.size;
        const progress = Math.round((offset / file.size) * 100);
        uploadStatus.updateYoutubeUpload(progress);
        
        // Calculate time estimate (estimate: 0.5 MB/s upload speed)
        const elapsed = (Date.now() - startTime) / 1000;
        const bytesPerSecond = offset / elapsed;
        const remainingBytes = file.size - offset;
        const estimatedSecondsRemaining = remainingBytes / bytesPerSecond;
        uploadStatus.updateYoutubeUpload(progress, estimatedSecondsRemaining);
        
        console.log(`📤 YouTube upload: ${progress}% (~${Math.round(estimatedSecondsRemaining)}s remaining)`);

        if (response.status === 200) {
          const result = await response.json();
          if (result.id) {
            const youtubeUrl = `https://www.youtube.com/watch?v=${result.id}`;
            console.log("✅ Video uploaded to YouTube:", youtubeUrl);

            // Update Firestore with YouTube URL
            const runRef = doc(db, 'runs', runId);
            await updateDoc(runRef, {
              youtubeUrl: youtubeUrl,
              uploadedToYouTube: true,
            });

            uploadStatus.updateYoutubeUpload(100);
            uploadStatus.completeYoutubeUpload();
            return;
          }
        }
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.message === 'Upload cancelled') {
        console.log("✅ Upload cancelled by user");
        // Don't complete - cancellation already handled by cancelYoutubeUpload
      } else {
        console.error("❌ YouTube upload failed:", error);
        uploadStatus.completeYoutubeUpload();
      }
    }
  }, [uploadStatus]);

  // Handle time updates from video player
  const handleTimeUpdate = useCallback((newTime) => {
    setCurrentTime(newTime);
  }, []);

  // Handle duration from video — video is source of truth for timeline when present
  const handleDurationChange = useCallback((newDuration) => {
    videoDurationReportedRef.current = true;
    setVideoDuration(newDuration || 0);
    setDuration(newDuration || 0);
  }, []);

  // Handle play/pause toggle
  const handlePlayPause = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  // Handle seeking from any synchronized component
  const handleSeek = useCallback((time) => {
    setCurrentTime(time);
  }, []);

  /** Match jack-benchmark.html zoomToEvent: ±1.5s padding on data timeline + seek video */
  const handleJackPackingEventSelect = useCallback(
    ({ startTime, endTime }) => {
      const st = Number(startTime);
      const en = Number(endTime);
      if (!Number.isFinite(st) || !Number.isFinite(en)) return;
      const padding = 1.5;
      setJackPlotXZoom({ min: st - padding, max: en + padding });
      handleSeek(st - (Number(dataOffset) || 0));
    },
    [handleSeek, dataOffset],
  );

  useEffect(() => {
    setJackPlotXZoom(null);
  }, [rideId]);

  // Handle reset layout
  const handleResetLayout = useCallback(() => {
    if (layoutRef.current && layoutRef.current.resetLayout) {
      layoutRef.current.resetLayout();
    }
  }, []);

  // Handle layout applied (reload to apply new layout)
  const handleLayoutApplied = useCallback(() => {
    window.location.reload();
  }, []);

  // Handle video offset change
  const handleVideoOffsetChange = useCallback(async (newOffset) => {
    setVideoOffset(newOffset);
    if (!isCreationMode && !isNonCloudRunId) {
      try {
        await updateDoc(doc(db, 'runs', rideId), { videoOffset: newOffset });
      } catch (error) {
        console.error('Failed to save video offset:', error);
      }
    }
  }, [rideId, isCreationMode, isNonCloudRunId]);

  // Handle data offset change
  const handleDataOffsetChange = useCallback(async (newOffset) => {
    setDataOffset(newOffset);
    if (!isCreationMode && !isNonCloudRunId) {
      try {
        await updateDoc(doc(db, 'runs', rideId), { dataOffset: newOffset });
      } catch (error) {
        console.error('Failed to save data offset:', error);
      }
    }
  }, [rideId, isCreationMode, isNonCloudRunId]);

  // Clean up export job listener on unmount
  useEffect(() => {
    return () => {
      if (exportJobUnsubscribeRef.current) {
        exportJobUnsubscribeRef.current();
        exportJobUnsubscribeRef.current = null;
      }
    };
  }, []);

  // Start export: create job, then either client-side record (Option A) or server (upload-then-export or direct URL).
  const handleStartExport = useCallback(async (templateId, options = {}) => {
    const uid = user?.uid || auth.currentUser?.uid;
    if (!uid || !rideId) return;
    if (typeof rideId === 'string' && (rideId.startsWith('local-') || rideId.startsWith('queued-inline-'))) {
      alert('This run is not in the cloud yet. Sync it (go online), then use Export again.');
      return;
    }
    if (isCreationMode && (!videoPreviewUrl || !rideData?.telemetryData?.length)) {
      setExportMessage('Please save the run first. After saving, open the run and use Export to create your video.');
      return;
    }
    setExportStarting(true);
    setExportProgress(null);
    setExportMessage(null);
    try {
      const runVideoUrl = rideData?.videoUrl || rideData?.video_url || (rideData?.videos?.[0]?.url);
      const isYt = runVideoUrl && (String(runVideoUrl).includes('youtube.com') || String(runVideoUrl).includes('youtu.be'));
      const canUseServer = !isCreationMode && runVideoUrl && !isYt;
      const useClientExport = isCreationMode
        ? !!(videoPreviewUrl && rideData?.telemetryData?.length)
        : !!(videoPreviewUrl && rideData?.telemetryData?.length && !isYouTubeUrl(videoPreviewUrl) && (!runVideoUrl || isYt));

      const jobRef = await addDoc(collection(db, 'exportJobs'), {
        userId: uid,
        runId: rideId,
        templateId,
        status: useClientExport ? 'processing' : 'pending',
        createdAt: serverTimestamp(),
      });
      const jobId = jobRef.id;

      const setupListener = () => {
        exportJobUnsubscribeRef.current = onSnapshot(doc(db, 'exportJobs', jobId), (snap) => {
          const data = snap.data();
          if (data?.status === 'ready' && data?.downloadURL) {
            if (exportJobUnsubscribeRef.current) {
              exportJobUnsubscribeRef.current();
              exportJobUnsubscribeRef.current = null;
            }
            const open = window.confirm('Your export is ready. Open the download link now?');
            if (open) window.open(data.downloadURL, '_blank');
          } else if (data?.status === 'failed' && data?.error) {
            if (exportJobUnsubscribeRef.current) {
              exportJobUnsubscribeRef.current();
              exportJobUnsubscribeRef.current = null;
            }
            alert('Export failed: ' + data.error);
          }
        });
      };
      setupListener();

      if (useClientExport) {
        startExportVideo({ phase: 'loading', jobId });
        setShowExportModal(false);
        setExportProgress('recording');
        const blob = await recordVideoWithOverlays({
          videoUrl: videoPreviewUrl,
          telemetryData: rideData.telemetryData,
          templateId,
          layoutConfig: options.layoutConfig,
          startTimeSeconds: options.startTimeSec,
          endTimeSeconds: options.endTimeSec,
          dataOffset: options.dataOffset ?? 0,
          visibleMetrics: options.visibleMetrics,
          onProgress: (msg) => {
            if (typeof msg === 'object' && msg != null) {
              setExportProgress('recording');
              updateExportVideo({
                phase: 'recording',
                progress: msg.progress ?? null,
                currentTime: msg.currentTime ?? null,
                duration: msg.duration ?? null,
                frameIndex: msg.frameIndex ?? null,
                estimatedTimeRemaining: msg.estimatedTimeRemaining ?? null,
              });
            } else {
              const phase = msg === 'Loading video…' ? 'loading' : 'recording';
              setExportProgress(phase === 'loading' ? 'loading' : 'recording');
              updateExportVideo({ phase });
            }
          },
        });
        updateExportVideo({ phase: 'uploading' });
        setExportProgress('uploading');
        const storagePath = `exports/${uid}/${jobId}.webm`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, blob, { contentType: 'video/webm' });
        const downloadURL = await getDownloadURL(storageRef);
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + 10);
        await updateDoc(doc(db, 'exportJobs', jobId), {
          status: 'ready',
          downloadURL,
          storagePath,
          expiresAt: Timestamp.fromDate(expiresAt),
          updatedAt: serverTimestamp(),
        });
        updateExportVideo({ phase: 'complete', downloadURL });
        setExportProgress(null);
        return;
      }

      // Only upload video to run if the run exists in Firestore (not in creation mode with create/new)
      if (!canUseServer && videoFile && rideData && (!runVideoUrl || isYt) && !isCreationMode && !isNonCloudRunId) {
        setExportProgress('uploading');
        const inputPath = `exports/${uid}/input_${rideId}_${Date.now()}.mp4`;
        const storageRef = ref(storage, inputPath);
        await uploadBytes(storageRef, videoFile);
        const downloadUrl = await getDownloadURL(storageRef);
        await updateDoc(doc(db, 'runs', rideId), { videoUrl: downloadUrl });
      }
      // Server export requires a saved run; in creation mode the run does not exist yet
      if (isCreationMode) {
        setExportProgress(null);
        setShowExportModal(false);
        alert('Please save the run first, then open it and start the export.');
        return;
      }
      setExportProgress('starting');
      const functions = getFunctions();
      const startRideExport = httpsCallable(functions, 'startRideExport');
      await startRideExport({
        jobId,
        runId: rideId,
        templateId,
        layoutConfig: options?.layoutConfig ?? undefined,
        startTimeSec: options?.startTimeSec ?? undefined,
        endTimeSec: options?.endTimeSec ?? undefined,
        dataOffset: options?.dataOffset ?? undefined,
        visibleMetrics: options?.visibleMetrics ?? undefined,
      });
      setExportProgress(null);
      setShowExportModal(false);
      alert("Export started. You'll be notified when it's ready.");
    } catch (error) {
      console.error('Export start failed:', error);
      setExportProgress(null);
      updateExportVideo({ phase: 'failed', error: error?.message || 'Unknown error' });
      alert('Failed to start export: ' + (error?.message || 'Unknown error'));
    } finally {
      setExportStarting(false);
    }
  }, [user, rideId, isCreationMode, isNonCloudRunId, rideData, videoFile, videoPreviewUrl, startExportVideo, updateExportVideo]);

  const buildRidePanelHeaderActionsBundle = ({
    creationVideoTrash,
    viewVideoTrash,
    creationTelemetryTrash,
    viewTelemetryTrash,
  }) => {
    const out = {};
    panelSlots.forEach((slot) => {
      const parts = [];
      if (panelSlots.length > 1) {
        parts.push(
          <button
            key="remove-panel"
            type="button"
            className="remove-file-button"
            onClick={(e) => {
              e.stopPropagation();
              removePanelSlot(slot.id);
            }}
            title="Remove panel"
            aria-label="Remove panel"
          >
            <FaTimes size={14} />
          </button>
        );
      }
      if (slot.contentId === 'video' && creationVideoTrash) {
        parts.push(
          <button
            key="rvid"
            type="button"
            className="remove-file-button"
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveVideo();
            }}
            title="Remove video"
            aria-label="Remove video"
          >
            <FaTrash size={14} />
          </button>
        );
      }
      if (slot.contentId === 'video' && viewVideoTrash) {
        parts.push(
          <button
            key="rvidv"
            type="button"
            className="remove-file-button"
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveVideo();
            }}
            title="Remove loaded video file"
            aria-label="Remove loaded video file"
          >
            <FaTrash size={14} />
          </button>
        );
      }
      if (slot.contentId === 'charts' && creationTelemetryTrash) {
        parts.push(
          <button
            key="rtel"
            type="button"
            className="remove-file-button"
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveTelemetry();
            }}
            title="Remove CSV, GPX, FIT, or other telemetry data"
            aria-label="Remove telemetry data"
          >
            <FaTrash size={14} />
          </button>
        );
      }
      if (slot.contentId === 'charts' && viewTelemetryTrash) {
        parts.push(
          <button
            key="rtelv"
            type="button"
            className="remove-file-button"
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveTelemetry();
            }}
            title="Remove telemetry data"
            aria-label="Remove telemetry data"
          >
            <FaTrash size={14} />
          </button>
        );
      }
      if (parts.length) out[slot.id] = <>{parts}</>;
    });
    return out;
  };

  /**
   * @param {*} slot { id, contentId }
   * @param {*} o.mode 'create' | 'view'
   * @param {*} o.local hasVideo, hasTelemetry, hasGPS (creation or view equivalents)
   * @param {*} o.viewExtras optional blob for view-mode video/map edge cases
   */
  const renderRidePanelSlotInner = (slot, o) => {
    const cid = slot.contentId;
    const runnable = isPanelContentRunnable(cid, panelAvailCtx);
    const suspensionTab = SUSPENSION_TAB_BY_CONTENT_ID[cid];
    const def = RIDE_PANEL_CATALOG[cid];

    if (!runnable) {
      let hint = 'This view needs data that is not available for this run.';
      if (def?.needsTelemetry && !panelAvailCtx.hasTelemetry) hint = 'Add telemetry data to enable this panel.';
      else if (def?.needsGps && panelAvailCtx.hasTelemetry && !panelAvailCtx.hasGps)
        hint = 'No GPS in this telemetry — map and location-based overlays are unavailable.';
      else if (def?.needsSuspensionData && !panelAvailCtx.hasSuspensionSamples)
        hint = 'No fork/shock traces in this run — suspension views need suspension telemetry.';
      return (
        <div className="ride-panel-placeholder">
          <p className="ride-panel-placeholder-title">{def?.label ?? cid}</p>
          <p className="ride-panel-placeholder-msg">{hint}</p>
        </div>
      );
    }

    if (cid === 'video') {
      if (o.mode === 'view') {
        const vxEarly = o.viewExtras;
        if (!vxEarly?.hasVideoOrPending) {
          return (
            <div className="ride-panel-placeholder">
              <p className="ride-panel-placeholder-msg">No video for this run.</p>
            </div>
          );
        }
      }
      if (o.mode === 'create') {
        const { hasVideo: hv } = o.local;
        if (hv && o.videoUrl) {
          return (
            <SyncedVideoPlayer
              videoUrl={o.videoUrl}
              currentTime={currentTime}
              isPlaying={isPlaying}
              onTimeUpdate={handleTimeUpdate}
              onDurationChange={handleDurationChange}
              videoOffset={0}
            />
          );
        }
        if (!hv) {
          return (
            <div
              className="dropzone-section"
              onDrop={handleUnifiedDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={triggerUnifiedFileBrowse}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  triggerUnifiedFileBrowse();
                }
              }}
            >
              <FaPlay className="dropzone-icon" />
              <p>Drop video or data file here</p>
            </div>
          );
        }
        return null;
      }
      const vx = o.viewExtras;
      if (!vx?.hasVideoOrPending) return null;
      if (vx.hasPlayableVideo) {
        return (
          <SyncedVideoPlayer
            videoUrl={vx.viewDirectVideoUrl || vx.viewVideoUrl || undefined}
            youtubeUrl={vx.viewYoutubeUrl || undefined}
            currentTime={currentTime}
            isPlaying={isPlaying}
            onTimeUpdate={handleTimeUpdate}
            onDurationChange={handleDurationChange}
            videoOffset={0}
          />
        );
      }
      if (!vx.videoPending) return null;
      if (vx.isLocalVideoOnly) {
        return (
          <div className="video-status-message dropzone-section" onDrop={handleLocalVideoDrop} onDragOver={(e) => e.preventDefault()}>
            <p className="video-status-title">Local video</p>
            <p className="video-status-hint">
              This run was saved without cloud video.
              {rideData.videoFileName ? ` Original file: ${rideData.videoFileName} (path was not stored).` : ''}{' '}
              Drop the video file here or choose a file to play it.
            </p>
            <label className="export-video-header-button" style={{ marginTop: 12, cursor: 'pointer', display: 'inline-flex' }}>
              <input type="file" accept="video/*" onChange={handleLocalVideoFileInput} style={{ display: 'none' }} />
              Choose video file
            </label>
          </div>
        );
      }
      return (
        <div className="video-status-message">
          {vx.isUploadingThisRun ? (
            <>
              <p className="video-status-title">Uploading video to YouTube…</p>
              <p className="video-status-progress">{vx.uploadProgress ?? 0}%</p>
              {vx.estimatedEta != null ? <p className="video-status-eta">~{Math.ceil(vx.estimatedEta)}s remaining</p> : null}
              <p className="video-status-hint">
                The video will appear here when the upload is complete. You can stay on this page or refresh later.
              </p>
            </>
          ) : (
            <>
              <p className="video-status-title">Video is processing</p>
              <p className="video-status-hint">
                The video will appear here when the upload to YouTube is complete. Refresh the page in a moment.
              </p>
            </>
          )}
        </div>
      );
    }

    if (cid === 'charts') {
      if (o.mode === 'create') {
        const { hasTelemetry: ht } = o.local;
        if (ht && jackAnalysisForRide?.ok && Array.isArray(jackAnalysisForRide.processedData) && jackAnalysisForRide.processedData.length > 0) {
          return (
            <JackSyncedPlotlyCharts
              processedData={jackAnalysisForRide.processedData}
              analysisResults={jackAnalysisForRide.analysisResults}
              currentTime={currentTime}
              dataOffset={dataOffset}
              onSeek={handleSeek}
              xZoomRange={jackPlotXZoom}
              onXZoomRangeCleared={() => setJackPlotXZoom(null)}
              analysisWindow={jackChartAnalysisWindow}
            />
          );
        }
        if (!ht) {
          return (
            <div
              className="dropzone-section"
              onDrop={handleUnifiedDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={triggerUnifiedFileBrowse}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  triggerUnifiedFileBrowse();
                }
              }}
            >
              <FaChartLine className="dropzone-icon" />
              <p>Drop video or data file here</p>
            </div>
          );
        }
        return null;
      }
      const vx = o.viewExtras;
      if (!vx?.hasTelemetry) return null;
      if (jackAnalysisForRide?.ok && Array.isArray(jackAnalysisForRide.processedData) && jackAnalysisForRide.processedData.length > 0) {
        return (
          <JackSyncedPlotlyCharts
            processedData={jackAnalysisForRide.processedData}
            analysisResults={jackAnalysisForRide.analysisResults}
            currentTime={currentTime}
            dataOffset={dataOffset}
            onSeek={handleSeek}
            xZoomRange={jackPlotXZoom}
            onXZoomRangeCleared={() => setJackPlotXZoom(null)}
            analysisWindow={jackChartAnalysisWindow}
          />
        );
      }
      return null;
    }

    if (cid === 'stats') {
      if (!o.local.hasTelemetry || !rideData?.telemetryData) return null;
      return <RideStatsPanel telemetryData={rideData.telemetryData} duration={duration} trainingZones={trainingZones} />;
    }

    if (cid === 'byb-stack-view') {
      return <BybTelemetryPanel telemetryData={rideData?.telemetryData || []} mode="stack" />;
    }

    if (cid === 'byb-imu-distrib') {
      return <BybTelemetryPanel telemetryData={rideData?.telemetryData || []} mode="imu-distrib" />;
    }

    if (cid === 'byb-imu-charts') {
      return <BybTelemetryPanel telemetryData={rideData?.telemetryData || []} mode="imu-charts" />;
    }

    if (cid === 'byb-brakes-speed') {
      return <BybTelemetryPanel telemetryData={rideData?.telemetryData || []} mode="brakes-speed" />;
    }

    if (cid === 'byb-custom-xy') {
      return <BybTelemetryPanel telemetryData={rideData?.telemetryData || []} mode="custom-xy" />;
    }

    if (suspensionTab) {
      if (!rideData?.telemetryData) return null;
      return (
        <SuspensionAnalysisPanel
          telemetryData={rideData.telemetryData}
          jackParams={jackRideParams}
          jackAnalysisResult={jackAnalysisForRide}
          onSeek={handleSeek}
          dataOffset={dataOffset}
          onJackPackingEventSelect={handleJackPackingEventSelect}
          embeddedTab={suspensionTab}
          initialAiInsight={rideData?.suspensionAiInsight || ''}
          onSaveAiInsight={handleSaveAiInsight}
        />
      );
    }

    if (cid === 'map') {
      if (o.mode === 'create') {
        const { hasGPS: hg } = o.local;
        if (hg && o.local.hasTelemetry) {
          return (
            <SyncedRouteMap telemetryData={rideData.telemetryData} currentTime={currentTime} onLocationClick={handleSeek} dataOffset={dataOffset} />
          );
        }
        return (
          <div className="ride-section map-section">
            <div
              className="map-placeholder dropzone-section"
              onDrop={handleUnifiedDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={triggerUnifiedFileBrowse}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  triggerUnifiedFileBrowse();
                }
              }}
            >
              <FaMap className="dropzone-icon" />
              <p>No map available - no GPS info found</p>
              <p className="map-placeholder-subtitle">Drop video or data file here to extract GPS</p>
            </div>
          </div>
        );
      }
      const vx = o.viewExtras;
      if (vx?.hasGPS && vx?.hasTelemetry) {
        return (
          <SyncedRouteMap telemetryData={rideData.telemetryData} currentTime={currentTime} onLocationClick={handleSeek} dataOffset={dataOffset} />
        );
      }
      return (
        <div className="ride-section map-section">
          <div
            className="map-placeholder dropzone-section"
            onDrop={handleUnifiedDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={triggerUnifiedFileBrowse}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                triggerUnifiedFileBrowse();
              }
            }}
          >
            <FaMap className="dropzone-icon" />
            <p>No map available - no GPS info found</p>
            <p className="map-placeholder-subtitle">Drop video or data file here to extract GPS</p>
          </div>
        </div>
      );
    }

    if (cid === 'comments') {
      return <CommentsPanel rideId={rideId} currentTime={currentTime} onSeek={handleSeek} />;
    }

    return null;
  };

  const ridePanelSectionClass = (cid) => {
    const m = {
      video: 'ride-section video-section',
      charts: 'ride-section graph-section',
      stats: 'ride-section stats-section',
      map: 'ride-section map-section',
      comments: 'ride-section comments-section',
    };
    if (m[cid]) return m[cid];
    if (SUSPENSION_TAB_BY_CONTENT_ID[cid]) return 'ride-section suspension-section';
    return 'ride-section';
  };

  if (loading) {
    return (
      <div className="ride-view-container">
        <div className="loading-spinner" role="status" aria-live="polite">
          <span className="ride-view-loading-spinner" aria-hidden="true" />
          <span>Loading run data...</span>
        </div>
      </div>
    );
  }

  if (!isCreationMode && offlineAccessBlocked) {
    return (
      <div className="ride-view-container">
        <div className="ride-view-header">
          <button type="button" className="back-button" onClick={() => navigate('/my-rides')} aria-label="Back to My Runs">
            <FaChevronLeft size={18} aria-hidden />
          </button>
        </div>
        <div style={{ padding: 24, maxWidth: 520, margin: '0 auto' }}>
          <h2 style={{ marginTop: 0 }}>Connection required</h2>
          <p style={{ lineHeight: 1.5 }}>
            You need to be connected to the internet to open this run on this device. Open it once while online to save it
            for offline viewing.
          </p>
          <button type="button" className="save-run-button" style={{ marginTop: 16 }} onClick={() => navigate('/my-rides')}>
            Back to My Runs
          </button>
        </div>
      </div>
    );
  }

  // In creation mode, show creation interface
  if (isCreationMode) {
    const hasVideo = !!(videoPreviewUrl || videoFile);
    const hasTelemetry = !!(rideData?.telemetryData && rideData.telemetryData.length > 0);
    const hasGPS = rideData?.telemetryData?.some(d => d.lat && d.lon);

    return (
      <div className="ride-view-container">
        {/* Header */}
        <div className="ride-view-header">
          <div className="ride-header-info">
            <div className="ride-title-wrapper">
              <div className="ride-title-row">
                <button type="button" className="back-button" onClick={() => navigate('/my-rides')} aria-label="Back to My Runs">
                  <FaChevronLeft size={18} aria-hidden />
                </button>
                {editingName ? (
                  <input
                    type="text"
                    className="editable-title-input"
                    value={runName}
                    onChange={(e) => setRunName(e.target.value)}
                    onBlur={() => setEditingName(false)}
                    onKeyPress={(e) => e.key === 'Enter' && setEditingName(false)}
                    autoFocus
                  />
                ) : (
                  <h1 
                    className="editable-title"
                    onClick={() => setEditingName(true)}
                  >
                    {runName || 'Unnamed Run'}
                  </h1>
                )}
              </div>
            </div>
          </div>
          <div className="ride-header-actions" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {hasVideo && (
              <button
                type="button"
                className="export-video-header-button"
                disabled={!hasTelemetry}
                title={hasTelemetry ? 'Export video (save the run first, then open it to export)' : 'Add telemetry data to enable export'}
                onClick={() => hasTelemetry && (setExportMessage(null), setShowExportModal(true))}
              >
                <FaVideo /> Export
              </button>
            )}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowImportModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <FaCloudDownloadAlt /> Import
            </button>
            <button 
              className="save-run-button"
              onClick={handleSaveRun}
              disabled={saving || (!hasVideo && !hasTelemetry)}
            >
              <FaSave /> {saving ? 'Saving...' : 'Save Run'}
            </button>
          </div>
        </div>

        <div className="view-controls">
          <button
            type="button"
            className="panel-toggles-dropdown-btn add-panel-slot-btn"
            onClick={addPanelSlot}
            title="Add another panel (pick content in the panel header)"
          >
            <FaColumns size={14} aria-hidden />
            Add panel
          </button>
          <div className="view-controls-right">
            {user && hasTelemetry ? (
              <BikeProfileAnalysisDropdown value={analysisBikeProfileId} onChange={setAnalysisBikeProfileId} />
            ) : null}
            {user && <LayoutDropdown onLayoutApplied={handleLayoutApplied} onResetLayout={handleResetLayout} />}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="ride-content-wrapper">
          <input
            ref={unifiedFileInputRef}
            type="file"
            accept="video/*,.csv,.CSV,.shq,.SHQ,.fit,.FIT,.gpx,.GPX,.byb,.BYB"
            style={{ display: 'none' }}
            onChange={handleUnifiedFileInput}
          />
          <ResizableLayout
            ref={layoutRef}
            panelTitleContent={ridePanelTitleContent}
            panelLabels={{}}
            panelHeaderActions={buildRidePanelHeaderActionsBundle({
              creationVideoTrash: hasVideo,
              viewVideoTrash: false,
              creationTelemetryTrash: hasTelemetry,
              viewTelemetryTrash: false,
            })}
          >
            {panelSlots.map((slot) => (
              <div key={slot.id} className={ridePanelSectionClass(slot.contentId)} data-panel-id={slot.id}>
                {renderRidePanelSlotInner(slot, {
                  mode: 'create',
                  local: { hasVideo, hasTelemetry, hasGPS },
                  videoUrl: videoPreviewUrl,
                })}
              </div>
            ))}
          </ResizableLayout>
        </div>

        {/* Add Metrics Modal - choose which streams to import */}
        {addMetricsModal && (
          <div className="add-metrics-modal-overlay" onClick={() => setAddMetricsModal(null)}>
            <div className="add-metrics-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Import metrics from file</h3>
              <p className="add-metrics-modal-desc">
                Choose which data streams to use. Conflicting metrics can be kept or replaced. New metrics can be imported or ignored.
              </p>
              <div className="add-metrics-conflict-list">
                {addMetricsModal.allNewMetrics.map((key) => {
                  const isConflict = addMetricsModal.conflicts.has(key) || (key === 'heart_rate' && addMetricsModal.conflicts.has('heartRate')) || (key === 'heartRate' && addMetricsModal.conflicts.has('heart_rate'));
                  const choiceVal = addMetricsModal.choices[key] ?? addMetricsModal.choices[key === 'heart_rate' ? 'heartRate' : key === 'heartRate' ? 'heart_rate' : key];
                  const label = METRIC_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                  const setChoice = (val) => {
                    setAddMetricsModal((m) => {
                      const upd = { ...m.choices, [key]: val };
                      if (key === 'heartRate' || key === 'heart_rate') upd.heartRate = upd.heart_rate = val;
                      return { ...m, choices: upd };
                    });
                  };
                  return (
                    <div key={key} className="add-metrics-conflict-row" data-conflict={isConflict}>
                      <span className="add-metrics-conflict-label">{label}{isConflict ? ' (conflict)' : ' (new)'}</span>
                      {isConflict ? (
                        <>
                          <label>
                            <input type="radio" name={`metric-${key}`} checked={choiceVal === 'keep'} onChange={() => setChoice('keep')} />
                            Keep current
                          </label>
                          <label>
                            <input type="radio" name={`metric-${key}`} checked={choiceVal === 'replace'} onChange={() => setChoice('replace')} />
                            Replace with new
                          </label>
                        </>
                      ) : (
                        <>
                          <label>
                            <input
                              type="radio"
                              name={`metric-${key}`}
                              checked={(addMetricsModal.choices[key] ?? 'import') === 'import'}
                              onChange={() => setAddMetricsModal((m) => ({ ...m, choices: { ...m.choices, [key]: 'import' } }))}
                            />
                            Import
                          </label>
                          <label>
                            <input
                              type="radio"
                              name={`metric-${key}`}
                              checked={addMetricsModal.choices[key] === 'ignore'}
                              onChange={() => setAddMetricsModal((m) => ({ ...m, choices: { ...m.choices, [key]: 'ignore' } }))}
                            />
                            Ignore
                          </label>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="add-metrics-modal-actions">
                <button type="button" className="add-metrics-btn-cancel" onClick={() => setAddMetricsModal(null)}>
                  Cancel
                </button>
                <button type="button" className="add-metrics-btn-apply" onClick={applyAddMetricsMerge}>
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Unified Playback Controls */}
        {(hasVideo || hasTelemetry) && (
          <SyncedControlBar
            currentTime={currentTime}
            duration={duration}
            isPlaying={isPlaying}
            onPlayPause={handlePlayPause}
            onSeek={handleSeek}
            dataOffset={dataOffset}
            onDataOffsetChange={hasTelemetry ? handleDataOffsetChange : undefined}
            comments={comments}
            analysisTrim={controlBarAnalysisTrim}
            onAnalysisTrimChange={hasTelemetry ? handleAnalysisTrimChange : undefined}
            analysisTrimDisabled={loading}
          />
        )}

        <ImportSourceModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImport={handleImportFromSource}
          stravaConnected={stravaConnected}
          brakeaceConnected={brakeaceConnected}
          defaultStep={location.state?.openImport === 'brakeace' ? 'brakeace-list' : undefined}
        />

        {/* Save video choice: cloud (YouTube) vs keep offline */}
        {saveVideoModalStep && (
          <div className="modal-overlay save-video-modal-overlay" onClick={() => saveVideoModalStep === 'choice' && setSaveVideoModalStep(null)}>
            <div className="save-video-modal" onClick={(e) => e.stopPropagation()}>
              {saveVideoModalStep === 'choice' && (
                <>
                  <h3 className="save-video-modal-title">Save video to cloud?</h3>
                  <p className="save-video-modal-message">
                    Store the video on YouTube (unlisted) so you can play it from any device. The upload will continue in the background after saving.
                  </p>
                  <div className="save-video-modal-actions">
                    <button type="button" className="save-video-btn save-video-btn-primary" onClick={() => { setSaveVideoModalStep(null); doSaveRun(true); }}>
                      Save to cloud (YouTube)
                    </button>
                    <button type="button" className="save-video-btn save-video-btn-secondary" onClick={() => setSaveVideoModalStep('offline-warning')}>
                      Keep offline
                    </button>
                    <button type="button" className="save-video-btn save-video-btn-ghost" onClick={() => setSaveVideoModalStep(null)}>
                      Cancel
                    </button>
                  </div>
                </>
              )}
              {saveVideoModalStep === 'offline-warning' && (
                <>
                  <h3 className="save-video-modal-title">Video will stay offline</h3>
                  <p className="save-video-modal-message save-video-modal-warning">
                    The video will not be stored online. You will need to re-import it locally when viewing this run later. The run will prompt for the missing video.
                  </p>
                  <div className="save-video-modal-actions">
                    <button type="button" className="save-video-btn save-video-btn-primary" onClick={() => { setSaveVideoModalStep(null); doSaveRun(false); }}>
                      Save without cloud
                    </button>
                    <button type="button" className="save-video-btn save-video-btn-ghost" onClick={() => setSaveVideoModalStep(null)}>
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <ExportVideoModal
          isOpen={showExportModal}
          onClose={() => { setShowExportModal(false); setExportMessage(null); setExportProgress(null); }}
          onStartExport={handleStartExport}
          hasTelemetry={hasTelemetry}
          hasGps={hasGPS}
          starting={exportStarting}
          progress={exportProgress}
          message={exportMessage}
          videoUrl={videoPreviewUrl}
          videoDuration={videoDuration}
          telemetryData={rideData?.telemetryData}
          chartVisibleMetrics={chartVisibleMetrics}
          dataOffset={dataOffset}
        />
      </div>
    );
  }

  // Regular view mode
  if (!rideData) {
    return (
      <div className="ride-view-container">
        <div className="error-message">Run not found</div>
      </div>
    );
  }

  const hasTelemetry = rideData.telemetryData?.length > 0;
  const hasGPS = rideData.telemetryData?.some(d => d.lat && d.lon);

  // Normalize to non-empty strings only; support both camelCase and snake_case from Firestore
  const str = (v) => (v != null && typeof v === 'string' && v.trim() ? v.trim() : null);
  const viewVideoUrl = str(rideData.videoUrl) || str(rideData.video_url) || (rideData.videos?.[0]?.url ? str(rideData.videos[0].url) : null);
  const viewYoutubeUrl = str(rideData.youtubeUrl) || str(rideData.youtube_url) || (viewVideoUrl && isYouTubeUrl(viewVideoUrl) ? viewVideoUrl : null) || (rideData.videos?.[0]?.url && isYouTubeUrl(rideData.videos[0].url) ? str(rideData.videos[0].url) : null);
  const viewDirectVideoUrl = str(rideData.localVideoUrl) || str(videoPreviewUrl) || (viewYoutubeUrl ? null : viewVideoUrl) || (rideData.videos?.[0]?.url && !isYouTubeUrl(rideData.videos[0].url) ? str(rideData.videos[0].url) : null);

  const hasVideo = !!(rideData.localVideoUrl || videoPreviewUrl || rideData.videoUrl || rideData.video_url || rideData.youtubeUrl || rideData.youtube_url || rideData.videos?.length > 0);
  const hasPlayableVideo = !!(viewDirectVideoUrl || viewYoutubeUrl);
  // Export when we have a direct cloud URL, YouTube URL (server uses yt-dlp), or when run was local-only and user dropped/selected a file
  const hasLocalFileForExport = (rideData.videoFileName || rideData.uploadCancelled) && (videoPreviewUrl || videoFile);
  const hasExportableVideo =
    (hasVideo && viewVideoUrl && !isYouTubeUrl(viewVideoUrl)) ||
    (hasVideo && viewYoutubeUrl) ||
    hasLocalFileForExport;

  // Video "pending": run expects a video but we don't have a playable URL yet (e.g. upload in progress or page loaded before Firestore had youtubeUrl)
  const isUploadingThisRun = uploadStatus.youtubeUpload.active && uploadStatus.youtubeUpload.runId === rideId;
  const videoPending = (rideData.videoFileName && !hasPlayableVideo) || isUploadingThisRun;
  const hasVideoOrPending = hasVideo || videoPending;
  // Run was saved with "Keep offline" (or has video file name but no cloud URL) – show "Local video" and prompt for file
  const isLocalVideoOnly = (rideData.videoFileName || rideData.uploadCancelled) && !hasPlayableVideo && !isUploadingThisRun;

  const viewBlobVideoLoaded =
    typeof videoPreviewUrl === 'string' && videoPreviewUrl.startsWith('blob:');
  const showViewVideoUnload = hasPlayableVideo && viewBlobVideoLoaded;

  const rideViewPanelExtras = {
    hasVideoOrPending,
    hasPlayableVideo,
    videoPending,
    isLocalVideoOnly,
    isUploadingThisRun,
    uploadProgress: uploadStatus.youtubeUpload.progress,
    estimatedEta: uploadStatus.youtubeUpload.estimatedTimeRemaining,
    viewDirectVideoUrl,
    viewYoutubeUrl,
    viewVideoUrl,
    hasTelemetry,
    hasGPS,
    rideData,
  };

  return (
    <div className="ride-view-container">
      {/* Header */}
      <div className="ride-view-header">
        <div className="ride-header-info">
          <div className="ride-title-wrapper">
            <div className="ride-title-row">
              <button type="button" className="back-button" onClick={() => navigate('/my-rides')} aria-label="Back to My Runs">
                <FaChevronLeft size={18} aria-hidden />
              </button>
              {canRenameInView && editingViewRunName ? (
                <input
                  type="text"
                  className="editable-title-input"
                  value={runName}
                  onChange={(e) => setRunName(e.target.value)}
                  onBlur={() => {
                    void finalizeViewRunName();
                    setEditingViewRunName(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.currentTarget.blur();
                    }
                    if (e.key === 'Escape') {
                      setRunName(rideData.runName || rideData.name || '');
                      setEditingViewRunName(false);
                    }
                  }}
                  autoFocus
                  aria-label="Run name"
                />
              ) : canRenameInView ? (
                <h1
                  className="editable-title"
                  onClick={() => {
                    setRunName(rideData.runName || rideData.name || '');
                    setEditingViewRunName(true);
                  }}
                  title="Click to rename"
                >
                  {rideData.runName || rideData.name || 'Unnamed Run'}
                </h1>
              ) : (
                <h1>{rideData.runName || rideData.name || 'Unnamed Run'}</h1>
              )}
              {!isCreationMode && (
                <div ref={headerMenuRef} className="ride-header-menu-wrap" style={{ marginLeft: 'auto', position: 'relative' }}>
                  <button
                    type="button"
                    className="ride-header-menu-trigger"
                    onClick={(e) => { e.stopPropagation(); setHeaderMenuOpen((v) => !v); }}
                    aria-haspopup="true"
                    aria-expanded={headerMenuOpen}
                    aria-label="Run menu"
                  >
                    <FaEllipsisV size={18} />
                  </button>
                  {headerMenuOpen && (
                    <div className="ride-header-menu-popover" role="menu">
                      {hasTelemetry ? (
                        <>
                          <button
                            type="button"
                            className="ride-header-menu-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              setHeaderMenuExportOpen((v) => !v);
                            }}
                            aria-expanded={headerMenuExportOpen}
                            aria-haspopup="true"
                            role="menuitem"
                          >
                            Export
                            <FaChevronRight
                              size={12}
                              style={{
                                marginLeft: 'auto',
                                opacity: 0.75,
                                transform: headerMenuExportOpen ? 'rotate(90deg)' : 'none',
                                transition: 'transform 0.15s ease',
                              }}
                              aria-hidden
                            />
                          </button>
                          {headerMenuExportOpen ? (
                            <>
                              <button
                                type="button"
                                className="ride-header-menu-item ride-header-menu-item--nested"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExportBybCsvFile();
                                }}
                                role="menuitem"
                              >
                                CSV file
                              </button>
                              <button
                                type="button"
                                className="ride-header-menu-item ride-header-menu-item--nested"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExportBybFile();
                                }}
                                role="menuitem"
                              >
                                BYB file (binary)
                              </button>
                            </>
                          ) : null}
                        </>
                      ) : null}
                      <button
                        type="button"
                        className="ride-header-menu-item ride-header-menu-item--danger"
                        onClick={() => handleDeleteRun()}
                        role="menuitem"
                      >
                        <FaTrash size={14} /> Delete
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            {rideData.description && <p className="ride-description">{rideData.description}</p>}
          </div>
        </div>
        {(hasVideo || hasUnsavedChanges) && (
          <div className="ride-header-actions">
            {(hasVideo || hasTelemetry) && (
              <button
                type="button"
                className="export-video-header-button"
                onClick={() => hasExportableVideo && (setExportMessage(null), setShowExportModal(true))}
                disabled={!hasExportableVideo}
                title={
                  hasExportableVideo
                    ? 'Export video with graph and/or GPS overlay'
                    : isLocalVideoOnly
                      ? 'Video is stored locally. Drop the video file above to play; export requires the video to be saved to the cloud.'
                      : 'Export requires a direct video URL (not YouTube). Upload the video to the cloud to use export.'
                }
              >
                <FaVideo /> Export
              </button>
            )}
            {hasUnsavedChanges && (
              <button
                className="save-run-button"
                onClick={handleSaveExistingRide}
                disabled={saving}
              >
                <FaSave /> {saving ? 'Saving...' : 'Save changes'}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="view-controls">
        <button
          type="button"
          className="panel-toggles-dropdown-btn add-panel-slot-btn"
          onClick={addPanelSlot}
          title="Add another panel (pick content in the panel header)"
        >
          <FaColumns size={14} aria-hidden />
          Add panel
        </button>
        <div className="view-controls-right">
          {user && hasTelemetry ? (
            <BikeProfileAnalysisDropdown value={analysisBikeProfileId} onChange={setAnalysisBikeProfileId} />
          ) : null}
          {user && <LayoutDropdown onLayoutApplied={handleLayoutApplied} onResetLayout={handleResetLayout} />}
        </div>
      </div>

      {/* Main Content Area with Resizable Layout */}
      <div className="ride-content-wrapper">
        <ResizableLayout
          ref={layoutRef}
          panelTitleContent={ridePanelTitleContent}
          panelLabels={{ 'ride-info-banner': 'Run info' }}
          panelHeaderActions={buildRidePanelHeaderActionsBundle({
            creationVideoTrash: false,
            viewVideoTrash: showViewVideoUnload,
            creationTelemetryTrash: false,
            viewTelemetryTrash: false,
          })}
        >
          {!isCreationMode && rideData && !hasTelemetry && (
            <div
              className="ride-section ride-no-telemetry-banner"
              data-panel-id="ride-info-banner"
              style={{ padding: '12px 16px', background: 'rgba(255,193,7,0.12)', border: '1px solid rgba(255,193,7,0.3)', borderRadius: 8, marginBottom: 8 }}
            >
              <p style={{ margin: 0, fontSize: 14, color: '#e8eaed', marginBottom: 10 }}>
                <strong>No telemetry data</strong> for this run. Graph, map, and stats are unavailable. Duration is taken from activity metadata for workout completion.
              </p>
              {(rideData.source === 'strava' && (rideData.strava?.activityId ?? rideData.strava?.activity_id)) ? (
                <button
                  type="button"
                  className="export-video-header-button"
                  disabled={fetchingStravaTelemetry}
                  onClick={handleFetchStravaTelemetry}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                >
                  <FaExternalLinkAlt />
                  {fetchingStravaTelemetry ? 'Fetching GPS & speed…' : 'Fetch GPS & speed from Strava'}
                </button>
              ) : null}
            </div>
          )}

          {panelSlots.map((slot) => (
            <div key={slot.id} className={ridePanelSectionClass(slot.contentId)} data-panel-id={slot.id}>
              {renderRidePanelSlotInner(slot, {
                mode: 'view',
                local: { hasVideo, hasTelemetry, hasGPS },
                viewExtras: rideViewPanelExtras,
              })}
            </div>
          ))}
        </ResizableLayout>
      </div>

      {/* Unified Playback Controls with Timeline */}
      {(hasVideoOrPending || hasTelemetry) && (
        <SyncedControlBar
          currentTime={currentTime}
          duration={duration}
          isPlaying={isPlaying}
          onPlayPause={handlePlayPause}
          onSeek={handleSeek}
          dataOffset={dataOffset}
          onDataOffsetChange={hasTelemetry ? handleDataOffsetChange : undefined}
          comments={comments}
          analysisTrim={controlBarAnalysisTrim}
          onAnalysisTrimChange={hasTelemetry ? handleAnalysisTrimChange : undefined}
          analysisTrimDisabled={false}
        />
      )}

      <ExportVideoModal
        isOpen={showExportModal}
        onClose={() => { setShowExportModal(false); setExportMessage(null); setExportProgress(null); }}
        onStartExport={handleStartExport}
        hasTelemetry={hasTelemetry}
        hasGps={hasGPS}
        starting={exportStarting}
        progress={exportProgress}
        message={exportMessage}
        videoUrl={videoPreviewUrl || viewDirectVideoUrl}
        videoDuration={videoDuration}
        telemetryData={rideData?.telemetryData}
        chartVisibleMetrics={chartVisibleMetrics}
        dataOffset={dataOffset}
      />

      {/* Add Metrics Modal (view mode) */}
      {addMetricsModal && (
        <div className="add-metrics-modal-overlay" onClick={() => setAddMetricsModal(null)}>
          <div className="add-metrics-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Import metrics from file</h3>
            <p className="add-metrics-modal-desc">
              Choose which data streams to use. Conflicting metrics can be kept or replaced. New metrics can be imported or ignored.
            </p>
            <div className="add-metrics-conflict-list">
              {addMetricsModal.allNewMetrics.map((key) => {
                const isConflict = addMetricsModal.conflicts.has(key) || (key === 'heart_rate' && addMetricsModal.conflicts.has('heartRate')) || (key === 'heartRate' && addMetricsModal.conflicts.has('heart_rate'));
                const choiceVal = addMetricsModal.choices[key] ?? addMetricsModal.choices[key === 'heart_rate' ? 'heartRate' : key === 'heartRate' ? 'heart_rate' : key];
                const label = METRIC_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                const setChoice = (val) => {
                  setAddMetricsModal((m) => {
                    const upd = { ...m.choices, [key]: val };
                    if (key === 'heartRate' || key === 'heart_rate') upd.heartRate = upd.heart_rate = val;
                    return { ...m, choices: upd };
                  });
                };
                return (
                  <div key={key} className="add-metrics-conflict-row" data-conflict={isConflict}>
                    <span className="add-metrics-conflict-label">{label}{isConflict ? ' (conflict)' : ' (new)'}</span>
                    {isConflict ? (
                      <>
                        <label>
                          <input type="radio" name={`metric-vm-${key}`} checked={choiceVal === 'keep'} onChange={() => setChoice('keep')} />
                          Keep current
                        </label>
                        <label>
                          <input type="radio" name={`metric-vm-${key}`} checked={choiceVal === 'replace'} onChange={() => setChoice('replace')} />
                          Replace with new
                        </label>
                      </>
                    ) : (
                      <>
                        <label>
                          <input type="radio" name={`metric-vm-${key}`} checked={(choiceVal ?? 'import') === 'import'} onChange={() => setChoice('import')} />
                          Import
                        </label>
                        <label>
                          <input type="radio" name={`metric-vm-${key}`} checked={choiceVal === 'ignore'} onChange={() => setChoice('ignore')} />
                          Ignore
                        </label>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="add-metrics-modal-actions">
              <button type="button" className="add-metrics-btn-cancel" onClick={() => setAddMetricsModal(null)}>Cancel</button>
              <button type="button" className="add-metrics-btn-apply" onClick={applyAddMetricsMerge}>Apply</button>
            </div>
          </div>
        </div>
      )}

      <ModalPopup
        isOpen={deleteRunModalOpen}
        title="Delete this run?"
        message="This removes the run from the cloud and your list. This cannot be undone. You will return to My Runs when it is removed."
        onClose={() => setDeleteRunModalOpen(false)}
        onCancel={() => setDeleteRunModalOpen(false)}
        cancelLabel="Cancel"
        onConfirm={() => {
          setDeleteRunModalOpen(false);
          performDeleteRun();
        }}
        confirmLabel="Delete"
      />
    </div>
  );
};

export default RideView;
